import { createAction, createReducer } from 'redux-act';
import { api_base_url, configurationStatuses, stepsConfig, configurationModuleType, busExpansionType, possible_starter_selections, filter_types, powerSupplySteps, protocols, pac_ranges, tm171amb, tm172sig, m171_need_amb, softwareStepsConfig, OPTION_CATEGORIES, m580_distributed_ranges, PAC_RUGGEDIZATION, LICENSE_MODELS, edgeClusterHead } from '../../constants';
import { API_INSTANCE } from '../../utils/service-http';
import { getAppLocale, getAppLocaleAsObject } from '../../utils/app-locale';
import Device from '../../models/Device';
import ConfigurationDTO from '../../models/dto/ConfigurationDTO';
import Configuration from '../../models/Configuration';
import DeviceDTO from '../../models/dto/DeviceDTO';
import ProductInformationCache from '../../persistence/description-cache';
import DeviceAccessory from '../../models/DeviceAccessory';
import convertName from '../../utils/convert-name';
import { logSavedConfigurationEvent, logFilterSelectedEvent, logShareConfiguration, logRenamedConfiguration, logVirtualPageViewDataLayerEvent } from '../../utils/analytics-logger';
import { appConfiguration } from '../../utils/app-starter';
import I10n from '../i10n';
import PowerSupplyGroup from '../../models/PowerSupplyGroup';
import PowerSupply from '../../models/PowerSupply';
import { buildNewSoftwareLicenses, removeSoftwareLicenceHelper, setSoftwareLicenceQuantityHelper } from './utils';

export const setActiveStep = createAction('SET_ACTIVE_STEP');
export const setStarterType = createAction('SET_STARTER_TYPE');
export const setConfigurationType = createAction('SET_CONFIGURATION_TYPE');
export const setConfigurationName = createAction('SET_CONFIGURATION_NAME');
export const setIslandName = createAction('SET_ISLAND_NAME');
export const setHeadQuestionsWithAnswers = createAction('SET_HEAD_QUESTIONS_WITH_ANSWERS');
export const setBaseOptions = createAction('SET_BASE_OPTIONS');
export const restoreConfiguration = createAction('RESTORE_CONFIGURATION');
export const restoreEmptyConfiguration = createAction('RESTORE_EMPTY_CONFIGURATION');
export const reopenConfiguration = createAction('REOPEN_CONFIGURATION');
export const setSelectedValuesTerminalBlocks = createAction('STORE_SELECTED_VALUES');


const DEFAULT_CONFIGURATION = {
  id: null,
  status: null,
  senderEmail: null,
  senderName: null,
  step: stepsConfig.start_screen,
  starterType: null,
  name: 'My configuration',
  rangeInfo: null,
  headModule: null,
  ioModules: [],
  comModules: [],
  cartridges: [],
  ioIslands: [],
  slc: null,
  rack: null,
  softwareVersion: null,
  baseOptions: null,
  headQuestionsWithAnswers: {},
  allowSavedModifications: false,
  seenSteps: [stepsConfig.head_device_selector_screen],
  campaignId: null,
  internal: null,
  urlParams: null,
  showSurveyDisclaimer: false,
  configurationType: null,
  softwareSelection: null,
  servicePlanActivated: null,
  showPrecommercializedProducts: false,
  softwareLicences: [],
  softwareLicenseSelections: [],
  licenseModel: null,
  softwareInfo: null,
};

export const configuration = createReducer(function (on, off) {
  on(setActiveStep, (state, payload) => {
    return {
      ...state,
      step: payload,
    };
  });
  on(setStarterType, (state, payload) => {
    return {
      ...state,
      ...DEFAULT_CONFIGURATION,
      starterType: payload,
      seenSteps: [stepsConfig.head_device_selector_screen],
      headQuestionsWithAnswers: {},
      baseOptions: null
    };
  });
  on(setConfigurationType, (state, payload) => {
    return {
      ...state,
      ...DEFAULT_CONFIGURATION,
      configurationType: payload,
      softwareSelection: null,
      headQuestionsWithAnswers: {},
      baseOptions: null
    };
  });
  on(setConfigurationName, (state, payload) => {
    const newState = {
      ...state,
      name: convertName(payload),
    };
    return newState;
  });
  on(setHeadQuestionsWithAnswers, (state, payload) => {
    return {
      ...state,
      headQuestionsWithAnswers: payload,
    };
  });
  on(setBaseOptions, (state, payload) => {
    return {
      ...state,
      baseOptions: payload,
    };
  });
  on(restoreConfiguration, (state, payload) => {
    const references = new Configuration(payload).extractDistinctReferences();
    ProductInformationCache
      .loadReferencesDescriptions(references);
    const config = {
      ...state,
      ...payload,
      configurationType: payload.configurationType,
      softwareSelection: payload.softwareSelection,
      softwareInfo: payload.softwareInfo,
      licenseModel: payload.licenseModel,
      softwareLicences: payload.softwareLicences || [],
      softwareLicenseSelections: payload.softwareLicenseSelections || [],
      rack: payload.rack,
      slc: payload.slc
    };
    return config;
  })
  on(reopenConfiguration, (state, payload) => {
    const references = new Configuration(payload).extractDistinctReferences();
    ProductInformationCache
      .loadReferencesDescriptions(references);
    const config = {
      ...state,
      ...payload,
      configurationType: payload.configurationType,
      softwareSelection: payload.softwareSelection,
      softwareInfo: payload.softwareInfo,
      licenseModel: payload.licenseModel,
      softwareLicences: payload.softwareLicences || [],
      softwareLicenseSelections: payload.softwareLicenseSelections || [],
      headModule: payload.headModule,
      rack: payload.rack,
      slc: payload.slc,
      allowSavedModifications: false,
    };
    return config;
  })
  on(restoreEmptyConfiguration, (state, payload) => {
    const config = {
      ...state,
      ...payload,
      configurationType: payload.configurationType,
      softwareSelection: payload.softwareSelection,
      softwareInfo: payload.softwareInfo,
      licenseModel: payload.licenseModel,
      softwareLicences: payload.softwareLicences || [],
      softwareLicenseSelections: payload.softwareLicenseSelections || [],
      rack: payload.rack,
    };
    return config;
  });
}, DEFAULT_CONFIGURATION);

export function changeSoftwareVersion(configuration, newSoftwareVersion) {
  return (dispatch, getState) => {

    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
      softwareVersion: newSoftwareVersion,
    };
    return updateConfiguration(newConfiguration, dispatch, getState, false);
  }
}

export function updateConfigurationRedboxes(configuration, redboxId) {

  let updatedConfig = JSON.parse(JSON.stringify(configuration));

  for (const redbox of (updatedConfig.redboxes || [])) {
    redbox.id = redboxId;
  }

  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}

export function shareConfiguration(configId, senderEmail, senderName, toEmail, toName, message, captchaResponse, logEvent, subject) {
  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
  return API_INSTANCE.post(`${api_base_url}/configurations/${configId}/share?locale=${getAppLocale()}${mainOverrideString}`,
    {
      senderName,
      senderEmail,
      recipientName: toName,
      recipientEmail: toEmail,
      message,
      subject,
      linkToTool: `${appConfiguration.backUrl}?configurationId=${configId}`,
      captchaResponse,
    }).then(() => {
      if (logEvent) {
        logShareConfiguration(configId);
      }
    });
}

function _cancelConfiguration(configId) {
  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
  const seamlessIdString = appConfiguration.seamlessSession ? `&seamlessId=${appConfiguration.seamlessSession.id}` : '';
  return API_INSTANCE.patch(`${api_base_url}/configurations/${configId}/cancel?locale=${getAppLocale()}${mainOverrideString}${seamlessIdString}`,
    {
    });

}

export function cancelConfiguration(configId) {
  return (dispatch) => {
    dispatch(saveConfigurationStarted());
    return _cancelConfiguration(configId).then((success) => {
      dispatch(saveConfigurationSuccess());
      return success.data.payload;
    }).catch((error) => {
      dispatch(saveConfigurationFailed());
      throw error;
    });
  }
}

export function renameConfiguration(configId, configurationName) {
  return (dispatch) => {
    const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
    const seamlessIdString = appConfiguration.seamlessSession ? `&seamlessId=${appConfiguration.seamlessSession.id}` : '';
    const hostString = `&host=${buildHost()}`;
    dispatch(saveConfigurationStarted());
    return API_INSTANCE.patch(`${api_base_url}/configurations/${configId}/rename?locale=${getAppLocale()}${mainOverrideString}${seamlessIdString}${hostString}`,
      {
        configurationName
      }).then((success) => {
        dispatch(setConfigurationName(configurationName));
        dispatch(saveConfigurationSuccess());
        logRenamedConfiguration(configId);
        return success.data.payload;
      }).catch((error) => {
        dispatch(saveConfigurationFailed());
        throw error;
      });
  }
}

export function duplicateConfiguration(configId) {
  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
  const seamlessIdString = appConfiguration.seamlessSession ? `&seamlessId=${appConfiguration.seamlessSession.id}` : '';
  const hostString = `&host=${buildHost()}`;
  return API_INSTANCE.post(`${api_base_url}/configurations/${configId}/duplicate?locale=${getAppLocale()}${mainOverrideString}${seamlessIdString}${hostString}`).then((success) => {
    return success.data.payload;
  }).catch((error) => {
    throw error;
  });
}

export function deleteConfiguration(configId) {
  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
  const seamlessIdString = appConfiguration.seamlessSession ? `&seamlessId=${appConfiguration.seamlessSession.id}` : '';
  return API_INSTANCE.delete(`${api_base_url}/configurations/${configId}/delete?locale=${getAppLocale()}${mainOverrideString}${seamlessIdString}`).then((success) => {
    return success.data.payload;
  }).catch((error) => {
    throw error;
  });
}

export function changeSoftwareAccessories(configuration, softwareList) {
  return (dispatch, getState) => {
    const newConfiguration = JSON.parse(JSON.stringify(configuration));
    const oldConfiguationAccessories = newConfiguration.accessories;
    newConfiguration.accessories = {
      ...oldConfiguationAccessories,
      softwareList
    };

    return updateConfiguration(newConfiguration, dispatch, getState, false);
  }
}


/**
 * Duplicates the cluster (io module block) given as parameter at the end of
 * the given io modules (with correct slots).
 *
 * @param {ModuleBlock} edgeCluster cluster to duplication
 * @param {number} nextSlot last I/O modules slot
 * @param {Device[]} ioModules I/O modules list where the cluster will be duplcated
 */
const _duplicateEdgeCluster = (edgeCluster, nextSlot, ioModules) => {
  for (let i = 0; i < edgeCluster.modules.length; i++) {
    const duplicatedModule = JSON.parse(JSON.stringify(edgeCluster.modules[i]));
    duplicatedModule.slot = (nextSlot + i);
    ioModules.push(duplicatedModule);
  }
}

/*** MODULE COMMON ***********/

const _duplicateModule = (configuration, slotToDuplicate, moduleType) => {
  let newConfiguration;
  if (moduleType === configurationModuleType.io_module) {
    // Deep copy the module
    const targettedIoModule = JSON.parse(JSON.stringify(configuration.ioModules.find(m => m.slot === slotToDuplicate)));
    const ioModulesTMP = [...configuration.ioModules];
    const nextSlot = ioModulesTMP.map(d => d.slot).reduce((a, b) => a > b ? a : b, 0) + 1;

    if (targettedIoModule.productType === edgeClusterHead) {
      const clusterToDuplicate = configuration.ioModuleBlocks.find(block => block.modules.map(m => m.slot).includes(slotToDuplicate));

      if (clusterToDuplicate && clusterToDuplicate.modules) {
        _duplicateEdgeCluster(clusterToDuplicate, nextSlot, ioModulesTMP);
      }
    } else {
      targettedIoModule.slot = nextSlot
      ioModulesTMP.push(targettedIoModule);
    }

    newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
      ioModules: ioModulesTMP,
    };

  } else if (moduleType === configurationModuleType.com_module) {
    // Deep copy the module
    const targettedComModule = JSON.parse(JSON.stringify(configuration.comModules.find(m => m.slot === slotToDuplicate)));
    const comModulesTMP = [...configuration.comModules];
    targettedComModule.slot = comModulesTMP.map(d => d.slot).reduce((a, b) => a > b ? a : b, 0) + 1;
    comModulesTMP.push(targettedComModule);
    newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
      comModules: comModulesTMP,
    };
  }
  return newConfiguration;
}
export function duplicateModule(configuration, ...args) {
  const updatedConfig = _duplicateModule(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}

const clearAccessories = (device) => {
  device.accessories = [];
  device.auxiliaries = [];
  device.sensors = [];
  device.spareParts = [];
  device.options = [];
}

const _setRack = (configuration, rackId) => {
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));
  duplicatedConfiguration.rack = {
    id: rackId
  };

  return {
    ...duplicatedConfiguration
  };
}

export function setRack(configuration, ...args) {
  const updatedConfig = _setRack(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}

export function addLocalRackForIsland(configuration, islandSlot, rackId) {
  let newConfiguration = JSON.parse(JSON.stringify(configuration));

  const island = newConfiguration.ioIslands.find(isl => isl.slot === islandSlot);
  const islandConfig = island?.configuration;

  const updatedIslandConfig = _addLocalRack(islandConfig, rackId);
  const ioIslandsTMP = [...newConfiguration.ioIslands];
  ioIslandsTMP.filter(isl => isl.slot === islandSlot).forEach(isl => isl.configuration = updatedIslandConfig);
   newConfiguration = {
    ...JSON.parse(JSON.stringify(newConfiguration)),
    ioIslands: ioIslandsTMP,
  };

  return (dispatch, getState) => {
    return updateConfiguration(newConfiguration, dispatch, getState, true).then(() => {
    });
  }
}

function _addLocalRack(configuration, rackId) {
  const newConfiguration = JSON.parse(JSON.stringify(configuration));

  const mainConfigModuleIds = newConfiguration.ioModules.map(mod => mod.id);

  // Automatically add the PAC transmitter module if it is not present on the main rack. This module should be placed on the last slot of the main rack.
  // Care about harsh
  if (!mainConfigModuleIds.includes('BMXXBE1000') && !mainConfigModuleIds.includes('BMXXBE1000H')) {
    newConfiguration.ioModules.push({
      id: newConfiguration.rack?.ruggedization === PAC_RUGGEDIZATION.HARSH ? 'BMXXBE1000H' : 'BMXXBE1000',
      slot: newConfiguration.rack.maxIoModules
    });
  }

  const newLocalRackConfiguration = {
    ...DEFAULT_IO_ISLAND,
    name: '',
    headModule: { id: newConfiguration.rack?.ruggedization === PAC_RUGGEDIZATION.HARSH ? 'BMXXBE1000H' : 'BMXXBE1000'},
    rangeInfo: { rangeName: pac_ranges.x80, },
    starterType: possible_starter_selections.PAC,
    softwareVersion: null,
    rack: {
      id: rackId
    },
  };
  const localRacksTMP = [...newConfiguration.localRacks];
  localRacksTMP.push(newLocalRackConfiguration);

  return {
    ...JSON.parse(JSON.stringify(newConfiguration)),
    localRacks: localRacksTMP
  };
}

export function addLocalRack(configuration, rackId) {

  const newConfiguration = _addLocalRack(configuration, rackId);

  return (dispatch, getState) => {
    return updateConfiguration(newConfiguration, dispatch, getState, true).then(() => {
    });
  }
}

export function setIslandRack(configuration, rackId) {
  const newConfiguration = JSON.parse(JSON.stringify(configuration));

  const usedSlots = newConfiguration.ioIslands.map(isl => isl.slot);
  const newIOIslandSlot = Math.max(...[0, ...usedSlots]) + 1;

  let newName = `${I10n.getOrDefault('pac_io_island.default_name', DEFAULT_IO_ISLAND.name, 'data')} ${newIOIslandSlot}`;
  const alreadyExistingNameAmount = newConfiguration.ioIslands.filter(isl => isl?.configuration?.name === newName).length;
  if (alreadyExistingNameAmount > 0) {
    newName += ` (${alreadyExistingNameAmount})`;
  }

  const mainConfigModuleIds = newConfiguration.ioModules.map(mod => mod.id);

  // Automatically add the PAC transmitter module if it is not present on the main rack. This module should be placed on the last slot of the main rack.
  if (!mainConfigModuleIds.includes('BMXXBE1000') && !mainConfigModuleIds.includes('BMXXBE1000H')) {
    newConfiguration.ioModules.push({
      id: newConfiguration.rangeInfo.rangeName === pac_ranges.m580 || newConfiguration.rangeInfo.rangeName === pac_ranges.m340 ? 'BMXXBE1000' : 'BMXXBE1000H',
      slot: newConfiguration.rack.maxIoModules
    });
  }

  const newIOIsland = {
    slot: newIOIslandSlot,
    protocol: { name: protocols.xBus, },
    configuration: {
      ...DEFAULT_IO_ISLAND,
      name: newName,
      headModule: { id: newConfiguration.rangeInfo.rangeName === pac_ranges.m580 || newConfiguration.rangeInfo.rangeName === pac_ranges.m340 ? 'BMXXBE1000' : 'BMXXBE1000H' },
      rangeInfo: { rangeName: pac_ranges.x80, },
      starterType: possible_starter_selections.PAC,
      softwareVersion: null,
      rack: {
        id: rackId
      },
    },
    customOutputConsumption: 0
  };

  const ioIslandsTMP = [...newConfiguration.ioIslands];
  ioIslandsTMP.push(newIOIsland);

  return (dispatch, getState) => {
    const configurationToSave = {
      ...JSON.parse(JSON.stringify(newConfiguration)),
      ioIslands: ioIslandsTMP,
    }

    return updateConfiguration(configurationToSave, dispatch, getState, true).then(() => {
    });
  }
}

export function createRemoteAreaWithRIO(configuration, rio, protocolName, filters) {

  const newConfiguration = JSON.parse(JSON.stringify(configuration));

  const usedSlots = newConfiguration.ioIslands.map(isl => isl.slot);
  const newIOIslandSlot = Math.max(...[0, ...usedSlots]) + 1;

  const defaultRackName = m580_distributed_ranges.includes(configuration.rangeInfo?.rangeName) ? I10n.getOrDefault('pac_extension_rack.default_name', DEFAULT_IO_ISLAND.name, 'data') : I10n.getOrDefault('pac_io_island.default_name', DEFAULT_IO_ISLAND.name, 'data');

  let newName = `${defaultRackName} ${newIOIslandSlot}`;
  const alreadyExistingNameAmount = newConfiguration.ioIslands.filter(isl => isl?.configuration?.name === newName).length;
  if (alreadyExistingNameAmount > 0) {
    newName += ` (${alreadyExistingNameAmount})`;
  }

  const newIOIsland = {
    slot: newIOIslandSlot,
    protocol: { name: protocolName },
    configuration: {
      ...DEFAULT_IO_ISLAND,
      name: newName,
      headModule: { id: rio.id },
      starterType: possible_starter_selections.PAC,
      softwareVersion: null,
    },
    customOutputConsumption: 0
  };

  const ioIslandsTMP = [...newConfiguration.ioIslands];
  ioIslandsTMP.push(newIOIsland);

  return (dispatch, getState) => {
    const configurationToSave = {
      ...JSON.parse(JSON.stringify(newConfiguration)),
      ioIslands: ioIslandsTMP,
    }
    return updateConfiguration(configurationToSave, dispatch, getState, true).then((result) => {
      logFilterSelectedEvent(newConfiguration, filter_types.io_island, filters);
      return result.configuration;
    });
  }
}

export function massUpdateTerminalBlockSettings(configuration, electricalConnection, cover, applyToAll) {

  let updatedConfig = JSON.parse(JSON.stringify(configuration));

  updatedConfig.ioModules.forEach((module, moduleIndex) => {
    module.slottedAdditionalDevices.filter(slotted => slotted.layer === 1).forEach((device, deviceIndex) => {
      if (applyToAll || !device.selectedDeviceId) {
        const selectedDevice = device.possibleDevices.find(d =>
          (electricalConnection === undefined || d.device.electricalConnection === electricalConnection) &&
          (cover === undefined || d.device.cover === cover)
        );

        if (selectedDevice) {
          device.selectedDeviceId = selectedDevice.device.id;
        }
      }

    });
  });

  if (updatedConfig.terminalBlockPreferences) {
    updatedConfig.terminalBlockPreferences.cover = cover;
    updatedConfig.terminalBlockPreferences.electricalConnection = electricalConnection;
  }

  return (dispatch, getState) => {
    return updateConfiguration(updatedConfig, dispatch, getState, false);
  };
}

export function updateTerminalBlockPreferences(configuration, electricalConnection, cover) {
  let updatedConfig = JSON.parse(JSON.stringify(configuration));

  if (updatedConfig.terminalBlockPreferences) {
    updatedConfig.terminalBlockPreferences.cover = cover;
    updatedConfig.terminalBlockPreferences.electricalConnection = electricalConnection;
  }


  return (dispatch, getState) => {
    return updateConfiguration(updatedConfig, dispatch, getState, false);
  };
}

export function setIslandRackForRemote(configuration, islandSlot, rackId) {
  const newConfiguration = JSON.parse(JSON.stringify(configuration));
  newConfiguration.ioIslands.filter(isl => isl.slot === islandSlot).forEach(isl => isl.configuration = {
    ...isl.configuration,
    rack: {
      id: rackId
    }
  });

  return (dispatch, getState) => {
    const configurationToSave = {
      ...JSON.parse(JSON.stringify(newConfiguration)),
    }
    return updateConfiguration(configurationToSave, dispatch, getState, true).then(() => {
    });
  }
}

const _removeLocalRack = (configuration, rackLocation) => {

  // Deep copy the configuration
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));
  const correctRackLocation = rackLocation - 1;
  if (rackLocation && duplicatedConfiguration.localRacks[correctRackLocation]) {
    duplicatedConfiguration.localRacks = duplicatedConfiguration.localRacks.filter((localRack, idx) => idx !== correctRackLocation);

    return duplicatedConfiguration;
  }
}

export function removeLocalRack(configuration, rackLocation) {
  const updatedConfig = _removeLocalRack(configuration, rackLocation);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}

export function removeIslandLocalRack(configuration, islandSlot, rackLocation) {
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));
  duplicatedConfiguration.ioIslands.map(island => {
    if (island.slot === islandSlot) {
      island.configuration = _removeLocalRack(island.configuration, rackLocation);
    }
    return island;
  });

  return (dispatch, getState) => updateConfiguration(duplicatedConfiguration, dispatch, getState, true);
}

const _replaceModule = (configuration, slotToReplace, moduleType, newReferenceID, rackLocation) => {

  // Deep copy the configuration
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));

  if (rackLocation && duplicatedConfiguration.localRacks[rackLocation - 1]) {
    const correctRackLocation = rackLocation - 1;
    duplicatedConfiguration.localRacks = duplicatedConfiguration.localRacks.map((localRack, idx) => {
      if (idx === correctRackLocation) {
        return _replaceModule(localRack, slotToReplace, moduleType, newReferenceID, null);
      } else {
        return localRack;
      }
    });
    return duplicatedConfiguration;
  }

  if (moduleType === configurationModuleType.head_module) {
    duplicatedConfiguration.headModule.id = newReferenceID;
    duplicatedConfiguration.accessories.softwareList = [];
    clearAccessories(duplicatedConfiguration.headModule);
  } else if (moduleType === configurationModuleType.io_module) {
    duplicatedConfiguration.ioModules.filter(m => m.slot === slotToReplace).forEach(m => { m.id = newReferenceID; clearAccessories(m); });
  }
  else if (moduleType === configurationModuleType.com_module) {

    // When replacing a PAC com module (i.e, a power supply), we replace all the power supplies (redundants).
    if (duplicatedConfiguration?.starterType === possible_starter_selections.PAC) {
      duplicatedConfiguration.comModules = [];
      for (let i = 1; i <= duplicatedConfiguration?.rack?.maxPowerSupplies; i++) {
        const newModule = { id: newReferenceID }
        newModule.slot = i;
        duplicatedConfiguration.comModules.push(newModule);
      }

    } else {
      const modToReplace = configuration.comModules.find(mod => mod.slot === slotToReplace);
      duplicatedConfiguration.comModules.filter(m => m.slot === slotToReplace).forEach(m => { m.id = newReferenceID; clearAccessories(m); });

      // Special case to replace M172 AMB module, remove all SIG modules
      if (modToReplace && m171_need_amb === configuration?.headModule?.productType && modToReplace.id === tm171amb) {
        duplicatedConfiguration.comModules = duplicatedConfiguration.comModules.filter(m => m.id !== tm172sig);
      }
    }
  }
  return {
    ...duplicatedConfiguration,
  };
}

export function replaceModule(configuration, ...args) {
  const updatedConfig = _replaceModule(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}

const _updateModule = (configuration, slotToUpdate, moduleType, module) => {
  // Deep copy the configuration
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));

  if (moduleType === configurationModuleType.head_module) {
    duplicatedConfiguration.headModule = Object.assign({}, duplicatedConfiguration.headModule, module);
  } else if (moduleType === configurationModuleType.io_module) {
    const moduleIndex = duplicatedConfiguration.ioModules.findIndex(m => m.slot === slotToUpdate);
    if (moduleIndex !== -1) {
      duplicatedConfiguration.ioModules[moduleIndex] = Object.assign({}, duplicatedConfiguration.ioModules[moduleIndex], module);
    }
  } else if (moduleType === configurationModuleType.com_module) {
    const moduleIndex = duplicatedConfiguration.comModules.findIndex(m => m.slot === slotToUpdate);
    if (moduleIndex !== -1) {
      duplicatedConfiguration.comModules[moduleIndex] = Object.assign({}, duplicatedConfiguration.comModules[moduleIndex], module);
    }
  }
  return {
    ...duplicatedConfiguration,
  };
}
export function updateModule(configuration, ...args) {
  const updatedConfig = _updateModule(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}

const _replaceSelectedAdditionalDevice = (configuration, slotToUpdate, moduleType, layer, selectedDevice, rackLocation = null) => {
  // Deep copy the configuration
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));

  if (rackLocation && duplicatedConfiguration.localRacks[rackLocation - 1]) {
    const correctRackLocation = rackLocation - 1;
    duplicatedConfiguration.localRacks = duplicatedConfiguration.localRacks.map((localRack, idx) => {
      if (idx === correctRackLocation) {
        return _replaceSelectedAdditionalDevice(localRack, slotToUpdate, moduleType, layer, selectedDevice, rackLocation = null);
      } else {
        return localRack;
      }
    });

    return duplicatedConfiguration;
  }

  if (moduleType === configurationModuleType.head_module) {
    duplicatedConfiguration.headModule.slottedAdditionalDevices.filter(s => s.layer === layer).forEach(s => {
      s.selectedDeviceId = selectedDevice;
    });
  }

  if (moduleType === configurationModuleType.io_module) {
    duplicatedConfiguration.ioModules.filter(m => m.slot === slotToUpdate).forEach(m => {
      m.slottedAdditionalDevices.filter(s => s.layer === layer).forEach(s => {
        s.selectedDeviceId = selectedDevice;
      });
    });
  }

  return {
    ...duplicatedConfiguration,
  };
}

export function replaceSelectedAdditionalDevice(configuration, ...args) {
  const updatedConfig = _replaceSelectedAdditionalDevice(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}

const _insertModuleAtSlot = (configuration, slotToInsert, moduleType, moduleToInsert, quantity, rackLocation) => {

  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));

  if (rackLocation && duplicatedConfiguration.localRacks[rackLocation - 1]) {
    const correctRackLocation = rackLocation - 1;
    duplicatedConfiguration.localRacks = duplicatedConfiguration.localRacks.map((localRack, idx) => {
      if (idx === correctRackLocation) {
        return _insertModuleAtSlot(localRack, slotToInsert, moduleType, moduleToInsert, quantity, null);
      } else {
        return localRack;
      }
    });

    return duplicatedConfiguration;
  }

  let newConfiguration;
  if (moduleType === configurationModuleType.io_module) {
    let ioModulesTMP = [...duplicatedConfiguration.ioModules];

    for (let i = 0; i < quantity; ++i) {
      ioModulesTMP = ioModulesTMP.filter(m => m.slot !== slotToInsert + i);

      const newModule = Object.assign({}, moduleToInsert);
      newModule.slot = slotToInsert + i;
      ioModulesTMP.push(newModule);
    }

    newConfiguration = {
      ...duplicatedConfiguration,
      ioModules: ioModulesTMP,
    };
  }

  return newConfiguration;
}

export function insertModuleAtSlot(configuration, ...args) {
  const updatedConfig = _insertModuleAtSlot(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}

export function insertIslandModuleAtSlot(configuration, islandSlot, ...args) {
  const updatedConfig = updateIsland(configuration, islandSlot, _insertModuleAtSlot, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}

const _insertModulesBefore = (configuration, slotToInsert, moduleType, moduleToInsert, quantity) => {
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));
  let newConfiguration;
  if (moduleType === configurationModuleType.io_module) {
    const ioModulesTMP = [...duplicatedConfiguration.ioModules];
    // Increment the slot of modules which will come after the ones inserted.
    ioModulesTMP.filter(m => m.slot >= slotToInsert).forEach(m => m.slot += quantity);

    for (let i = 0; i < quantity; ++i) {
      const newModule = Object.assign({}, moduleToInsert);
      newModule.slot = slotToInsert + i;
      ioModulesTMP.push(newModule);
    }

    newConfiguration = {
      ...duplicatedConfiguration,
      ioModules: ioModulesTMP,
    };
  }
  else if (moduleType === configurationModuleType.com_module) {
    const comModulesTMP = [...duplicatedConfiguration.comModules];
    // Increment the slot of modules which will come after the ones inserted.
    comModulesTMP.filter(m => m.slot >= slotToInsert).forEach(m => m.slot += quantity);

    for (let i = 0; i < quantity; ++i) {
      const newModule = Object.assign({}, moduleToInsert);
      newModule.slot = slotToInsert + i;
      comModulesTMP.push(newModule);
    }

    newConfiguration = {
      ...duplicatedConfiguration,
      comModules: comModulesTMP,
    };
  }
  return newConfiguration;
}
export function insertModulesBefore(configuration, ...args) {
  const updatedConfig = _insertModulesBefore(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}

const _insertModulesAfter = (configuration, slotToInsert, moduleType, moduleToInsert, quantity) => {
  return _insertModulesBefore(configuration, slotToInsert + 1, moduleType, moduleToInsert, quantity);
}
export function insertModulesAfter(configuration, ...args) {
  const updatedConfig = _insertModulesAfter(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}

const _removeModule = (configuration, slotToRemove, moduleType, decreaseSlot = true, rackLocation = null) => {

  let newConfiguration;
  const configurationObject = new Configuration(JSON.parse(JSON.stringify(configuration)));

  if (rackLocation && configurationObject.localRacks[rackLocation - 1]) {
    const correctRackLocation = rackLocation - 1;
    configurationObject.localRacks = configurationObject.localRacks.map((localRack, idx) => {
      if (idx === correctRackLocation) {
        return _removeModule(localRack, slotToRemove, moduleType, decreaseSlot, null);
      } else {
        return localRack;
      }
    });
    return configurationObject;
  }

  if (moduleType === configurationModuleType.io_module) {
    const removedModule = new Device(configurationObject.ioModules.find(m => m.slot === slotToRemove));
    let ioModulesTMP = [...configurationObject.ioModules];
    // If the device is a bus expansion module, we delete all modules attached to its right side.
    if (removedModule.busExpansionType !== busExpansionType.none) {
      if (slotToRemove > 1) {
        // Special case to delete the transmitter when a receiver is deleted
        // Not applied to 'both' type
        const previousDevice = new Device(configurationObject.ioModules.find(m => m.slot === slotToRemove - 1));
        if (removedModule.busExpansionType === busExpansionType.receiver &&
          previousDevice.busExpansionType === busExpansionType.transmitter) {
          slotToRemove--;
        }
      }
      ioModulesTMP = ioModulesTMP.filter(m => m.slot < slotToRemove);
    } else {
      // If the deleted module is a power supply, then delete all modules in the same segment.
      if (removedModule.isPowerSupplier()) {
        // eslint-disable-next-line
        const [busSegment, nameSuffix] = configurationObject.findPowerSupplyBusSegmentOfModule(removedModule);
        const powerSupplyBusSegmentModules = busSegment?.modules || [];
        // Remove all modules in same segment.
        ioModulesTMP = ioModulesTMP.filter(m => !powerSupplyBusSegmentModules.find(psm => psm.slot === m.slot));
        // Decrease the slot of the following modules.
        ioModulesTMP.filter(m => m.slot > slotToRemove).forEach(m => m.slot -= powerSupplyBusSegmentModules.length);
      } else {
        ioModulesTMP = ioModulesTMP.filter(m => m.slot !== slotToRemove);

        if (decreaseSlot) {
          // Decrease the slot of the following modules.
          ioModulesTMP.filter(m => m.slot > slotToRemove).forEach(m => m.slot -= 1);
        }

      }
    }
    newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
      ioModules: ioModulesTMP,
    };
  } else if (moduleType === configurationModuleType.com_module) {

    let comModulesTMP = [...configuration.comModules];
    const modToRemove = comModulesTMP.find(mod => mod.slot === slotToRemove);


    // Special case to remove M172 AMB module, remove all com modules
    if (modToRemove && m171_need_amb === configuration?.headModule?.productType && modToRemove.id === tm171amb) {
      comModulesTMP = [];
    } else {
      comModulesTMP = comModulesTMP.filter(m => m.slot !== slotToRemove);

     // Decrease the slot of the following modules
      comModulesTMP.filter(m => m.slot > slotToRemove).forEach(m => m.slot -= 1);

    }

    newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
      comModules: comModulesTMP,
    };
  }
  return newConfiguration;
}
export function removeModule(configuration, ...args) {
  const updatedConfig = _removeModule(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}

const _setModuleWarranty = (configuration, slotToUpdate, moduleType, newWarranty) => {
  // Deep copy the configuration
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));

  if (moduleType === configurationModuleType.head_module) {
    duplicatedConfiguration.headModule.warranty = newWarranty;
  } else if (moduleType === configurationModuleType.io_module) {
    duplicatedConfiguration.ioModules.filter(m => m.slot === slotToUpdate).forEach(m => m.warranty = newWarranty);
  }
  else if (moduleType === configurationModuleType.com_module) {
    duplicatedConfiguration.comModules.filter(m => m.slot === slotToUpdate).forEach(m => m.warranty = newWarranty);
  }
  return {
    ...duplicatedConfiguration,
  };

}

export function setModuleWarranty(configuration, ...args) {
  const updatedConfig = _setModuleWarranty(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}

const _addModuleAccessory = (configuration, slotToUpdate, moduleType, newAccessory, quantity = 1) => {
  // Deep copy the configuration
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));
  let targettedModule;
  if (moduleType === configurationModuleType.head_module) {
    targettedModule = duplicatedConfiguration.headModule;
  } else if (moduleType === configurationModuleType.io_module) {
    targettedModule = duplicatedConfiguration.ioModules.find(module => slotToUpdate === module.slot);
  }
  else if (moduleType === configurationModuleType.com_module) {
    targettedModule = duplicatedConfiguration.comModules.find(module => slotToUpdate === module.slot);
  } else if (moduleType === configurationModuleType.power_supply) {
    try {
      targettedModule = duplicateConfiguration.powerSupplyManagement.powerSupplyGroups[slotToUpdate].powerSupply;
    } catch (error) { }
  }

  if (targettedModule) {
    targettedModule.accessories = _insertModuleAccessory(targettedModule.accessories, newAccessory, quantity);
   }

  return {
    ...duplicatedConfiguration,
  };
}

const _addModuleAuxiliary = (configuration, slotToUpdate, moduleType, newAccessory, quantity = 1) => {
  // Deep copy the configuration
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));
  let targettedModule;
  if (moduleType === configurationModuleType.head_module) {
    targettedModule = duplicatedConfiguration.headModule;
  } else if (moduleType === configurationModuleType.io_module) {
    targettedModule = duplicatedConfiguration.ioModules.find(module => slotToUpdate === module.slot);
  }
  else if (moduleType === configurationModuleType.com_module) {
    targettedModule = duplicatedConfiguration.comModules.find(module => slotToUpdate === module.slot);
  } else if (moduleType === configurationModuleType.power_supply) {
    try {
      targettedModule = duplicateConfiguration.powerSupplyManagement.powerSupplyGroups[slotToUpdate].powerSupply;
    } catch (error) { }
  }

  if (targettedModule) {
    targettedModule.auxiliaries = _insertModuleAccessory(targettedModule.auxiliaries, newAccessory, quantity);
  }

  return {
    ...duplicatedConfiguration,
  };
}

const _addModuleSensor = (configuration, slotToUpdate, moduleType, newAccessory, quantity = 1) => {
  // Deep copy the configuration
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));
  let targettedModule;
  if (moduleType === configurationModuleType.head_module) {
    targettedModule = duplicatedConfiguration.headModule;
  } else if (moduleType === configurationModuleType.io_module) {
    targettedModule = duplicatedConfiguration.ioModules.find(module => slotToUpdate === module.slot);
  }
  else if (moduleType === configurationModuleType.com_module) {
    targettedModule = duplicatedConfiguration.comModules.find(module => slotToUpdate === module.slot);
  } else if (moduleType === configurationModuleType.power_supply) {
    try {
      targettedModule = duplicateConfiguration.powerSupplyManagement.powerSupplyGroups[slotToUpdate].powerSupply;
    } catch (error) { }
  }

  if (targettedModule) {
    targettedModule.sensors = _insertModuleAccessory(targettedModule.sensors, newAccessory, quantity);
  }

  return {
    ...duplicatedConfiguration,
  };
}

const _addModuleSparePart = (configuration, slotToUpdate, moduleType, newAccessory, quantity = 1) => {
  // Deep copy the configuration
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));
  let targettedModule;
  if (moduleType === configurationModuleType.head_module) {
    targettedModule = duplicatedConfiguration.headModule;
  } else if (moduleType === configurationModuleType.io_module) {
    targettedModule = duplicatedConfiguration.ioModules.find(module => slotToUpdate === module.slot);
  }
  else if (moduleType === configurationModuleType.com_module) {
    targettedModule = duplicatedConfiguration.comModules.find(module => slotToUpdate === module.slot);
  } else if (moduleType === configurationModuleType.power_supply) {
    try {
      targettedModule = duplicateConfiguration.powerSupplyManagement.powerSupplyGroups[slotToUpdate].powerSupply;
    } catch (error) { }
  }

  if (targettedModule) {
    targettedModule.spareParts = _insertModuleAccessory(targettedModule.spareParts, newAccessory, quantity);
  }

  return {
    ...duplicatedConfiguration,
  };
}

const _addModuleOption = (configuration, slotToUpdate, moduleType, newAccessory, quantity = 1) => {
  // Deep copy the configuration
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));
  let targettedModule;
  if (moduleType === configurationModuleType.head_module) {
    targettedModule = duplicatedConfiguration.headModule;
  } else if (moduleType === configurationModuleType.io_module) {
    targettedModule = duplicatedConfiguration.ioModules.find(module => slotToUpdate === module.slot);
  }
  else if (moduleType === configurationModuleType.com_module) {
    targettedModule = duplicatedConfiguration.comModules.find(module => slotToUpdate === module.slot);
  } else if (moduleType === configurationModuleType.power_supply) {
    try {
      targettedModule = duplicateConfiguration.powerSupplyManagement.powerSupplyGroups[slotToUpdate].powerSupply;
    } catch (error) { }
  }

  if (targettedModule) {
    targettedModule.options = _insertModuleAccessory(targettedModule.options, newAccessory, quantity);
  }

  return {
    ...duplicatedConfiguration,
  };
}

const _insertModuleAccessory = (oldAccessories, newAccessory, quantity, type) => {
  const newAccessories = JSON.parse(JSON.stringify(oldAccessories));
  // Allow negative quantity in order to decrease
  if (quantity != null) {
    const existingAccessory = newAccessories.find(accessory => newAccessory === accessory.accessory);
    if (existingAccessory) {
      existingAccessory.quantity = Math.max(1, existingAccessory.quantity + quantity);
    } else if (quantity > 0) {
      const accessory = new DeviceAccessory({ accessory: newAccessory, quantity, subAccessories: [] });
      if (type === 'UPS') {
        const subAccessory = new DeviceAccessory({ accessory: 'XB005XPDR', quantity: 1 });
        accessory.subAccessories.push(subAccessory);
      }
      newAccessories.push(accessory);
    }
  }
  return newAccessories;
}

const _insertModuleSubAccessory = (oldSubAccessories, subAccessoryId, quantity, type) => {
  const newSubAccessories = oldSubAccessories;
  if (quantity != null) {
    if (oldSubAccessories.filter(subAccessory => subAccessoryId === subAccessory.accessory).length > 0) {
      newSubAccessories.filter(subAccessory => subAccessoryId === subAccessory.accessory).forEach(subAccessory => {
        subAccessory.quantity = Math.max(1, subAccessory.quantity + quantity);
        subAccessory.type = type; });
    } else if (quantity > 0) {
      newSubAccessories.push(new DeviceAccessory({ accessory: subAccessoryId, quantity, type }));
    }
  }
  return newSubAccessories;
}

const _addAccessoryCartToModule = (mod, accessoryCart) => {
  Object.keys(accessoryCart).forEach((catKey) => {
    const accessoriesInCategoryObj = accessoryCart[catKey];
    const accessoriesInCategory = Object.keys(accessoriesInCategoryObj);
    if (catKey === OPTION_CATEGORIES.WARRANTIES && accessoriesInCategory.length > 0) {
      mod.warranty = accessoriesInCategory[0];
    } else {
      let currentAccessories = mod[catKey];
      if (mod[catKey] != null) {
        const upsInGroup = currentAccessories.some(option => option.type === 'UPS');
        if (upsInGroup) {
          currentAccessories = currentAccessories.filter(option => option.type !== 'UPS');
        }
        accessoriesInCategory.forEach(accessoryKey => {
          const accesoryType = accessoriesInCategoryObj[accessoryKey].type;
          const upsInModule = Object.values(mod[catKey]).some(accessory => {
            return accessory.type === 'UPS';
          });
          if (!upsInModule || accessoryKey !== 'UPS') {
            mod[catKey] = _insertModuleAccessory(currentAccessories, accessoryKey, accessoriesInCategoryObj[accessoryKey].quantity, accesoryType);
          } else {
            mod[catKey] = _insertModuleSubAccessory(currentAccessories, accessoryKey, accessoriesInCategoryObj[accessoryKey], accesoryType);
          }
        })
      }
    }
  });

  return mod;
};

const _addAccessoryCartToModuleList = (moduleList, moduleId, accessoryCart, slotsToAdd) => {
  moduleList.forEach(mod => {
    if (mod.id === moduleId && slotsToAdd.includes(mod.slot)) {
      _addAccessoryCartToModule(mod, accessoryCart);
    }
  });
  return moduleList;
}

const _addAccessoryCartToConfiguration = (configuration, moduleId, moduleType, accessoryCart, slotsToAdd) => {
  if (moduleType === configurationModuleType.head_module) {
    configuration.headModule = _addAccessoryCartToModule(configuration.headModule, accessoryCart);
  } else if (moduleType === configurationModuleType.io_module) {
    configuration.ioModules = _addAccessoryCartToModuleList(configuration.ioModules, moduleId, accessoryCart, slotsToAdd);
  } else if (moduleType === configurationModuleType.com_module) {
    configuration.comModules = _addAccessoryCartToModuleList(configuration.comModules, moduleId, accessoryCart, slotsToAdd);
  } else if (moduleType === configurationModuleType.power_supply) {
    configuration.powerSupplyManagement.powerSupplyGroups.forEach((group, idx) => {
      if (slotsToAdd.includes(idx)) {
        _addAccessoryCartToModule(group.powerSupply, accessoryCart);
      }
    });
  } else if (moduleType === configurationModuleType.redbox) {
    _addAccessoryCartToMultipleRedboxes(configuration.redboxes, accessoryCart, slotsToAdd);
  }

  return configuration;
}

const _addAccessoryCartToMultipleRedboxes = (redboxes, accessoryCart, slotsToAdd) => {
  redboxes.forEach((redbox, index) => {
    if (slotsToAdd.includes(index)) {
      _addAccessoryCartToModule(redbox, accessoryCart);
    }
  });
};

export const _addAccessoryCartToMultipleModules = (configuration, moduleId, islandSlot, rackLocation, moduleType, accessoryCart, slotsToAdd) => {
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));

  if (islandSlot === 0) {
    if (rackLocation && duplicatedConfiguration.localRacks[rackLocation - 1]) {
      const correctRackLocation = rackLocation - 1;
      duplicatedConfiguration.localRacks = duplicatedConfiguration.localRacks.map((localRack, idx) => {
        if (idx === correctRackLocation) {
          return _addAccessoryCartToConfiguration(localRack, moduleId, moduleType, accessoryCart, slotsToAdd);
        } else {
          return localRack;
        }
      });
    } else {
      _addAccessoryCartToConfiguration(duplicatedConfiguration, moduleId, moduleType, accessoryCart, slotsToAdd);
    }
  } else {
    duplicatedConfiguration.ioIslands.map(island => {
      if (island.slot === islandSlot) {
        if (rackLocation && island.configuration.localRacks[rackLocation - 1]) {
          const correctRackLocation = rackLocation - 1;
          island.configuration.localRacks = island.configuration.localRacks.map((localRack, idx) => {
            if (idx === correctRackLocation) {
              return _addAccessoryCartToConfiguration(localRack, moduleId, moduleType, accessoryCart, slotsToAdd);
            } else {
              return localRack;
            }
          });
        } else {
          island.configuration = _addAccessoryCartToConfiguration(island.configuration, moduleId, moduleType, accessoryCart, slotsToAdd);
        }
      }
      return island;
    });
  }

  return {
    ...duplicatedConfiguration,
  };
}

export function addModuleAccessory(configuration, ...args) {
  const updatedConfig = _addModuleAccessory(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}

export function addSubAccessory(configuration, ...args) {
    const updatedConfig = _addSubAccessory(configuration, ...args);
    return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}

export function removeSubAccessory(configuration, ...args) {
  const updatedConfig = _removeSubAccessory(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}

export function addModuleAuxiliary(configuration, ...args) {
  const updatedConfig = _addModuleAuxiliary(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}

export function addModuleSensor(configuration, ...args) {
  const updatedConfig = _addModuleSensor(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}

export function addModuleSparePart(configuration, ...args) {
  const updatedConfig = _addModuleSparePart(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}

export function addModuleOption(configuration, ...args) {
  const updatedConfig = _addModuleOption(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}

export function addAccessoryCartToMultipleModules(configuration, moduleId, islandSlot, rackLocation, moduleType, accessoryCart, slotsToAdd) {
  const updatedConfig = _addAccessoryCartToMultipleModules(configuration, moduleId, islandSlot, rackLocation, moduleType, accessoryCart, slotsToAdd);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}

const _removeModuleAccessory = (configuration, slotToUpdate, moduleType, accessoryToRemove) => {
  // Deep copy the configuration
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));
  let targettedModule;
  if (moduleType === configurationModuleType.head_module) {
    targettedModule = duplicatedConfiguration.headModule;
  } else if (moduleType === configurationModuleType.io_module) {
    targettedModule = duplicatedConfiguration.ioModules.find(module => slotToUpdate === module.slot);
  } else if (moduleType === configurationModuleType.com_module) {
    targettedModule = duplicatedConfiguration.comModules.find(module => slotToUpdate === module.slot);
  } else if (moduleType === configurationModuleType.power_supply) {
    try {
      targettedModule = duplicatedConfiguration.powerSupplyManagement.powerSupplyGroups[slotToUpdate].powerSupply;
    } catch (error) { }
  } else if (moduleType === configurationModuleType.redbox) {
    duplicatedConfiguration.redboxes.forEach((redbox, index) => {
      if (redbox.slot === undefined) {
        redbox.slot = index;
      }
    });
    targettedModule = duplicatedConfiguration.redboxes.find(module => slotToUpdate === module.slot);
  }

  if (targettedModule) {
    // Keep only the needed accessories
    targettedModule.accessories = targettedModule.accessories.filter(accessory => accessoryToRemove !== accessory.accessory);
  }

  return {
    ...duplicatedConfiguration,
  };
}

const _removeModuleAuxiliary = (configuration, slotToUpdate, moduleType, accessoryToRemove) => {
  // Deep copy the configuration
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));
  let targettedModule;
  if (moduleType === configurationModuleType.head_module) {
    targettedModule = duplicatedConfiguration.headModule;
  } else if (moduleType === configurationModuleType.io_module) {
    targettedModule = duplicatedConfiguration.ioModules.find(module => slotToUpdate === module.slot);
  } else if (moduleType === configurationModuleType.com_module) {
    targettedModule = duplicatedConfiguration.comModules.find(module => slotToUpdate === module.slot);
  } else if (moduleType === configurationModuleType.power_supply) {
    try {
      targettedModule = duplicatedConfiguration.powerSupplyManagement.powerSupplyGroups[slotToUpdate].powerSupply;
    } catch (error) { }
  } else if (moduleType === configurationModuleType.redbox) {
    duplicatedConfiguration.redboxes.forEach((redbox, index) => {
      if (redbox.slot === undefined) {
        redbox.slot = index;
      }
    });
    targettedModule = duplicatedConfiguration.redboxes.find(module => slotToUpdate === module.slot);
  }

  if (targettedModule) {
    // Keep only the needed accessories
    targettedModule.auxiliaries = targettedModule.auxiliaries.filter(accessory => accessoryToRemove !== accessory.accessory);
  }

  return {
    ...duplicatedConfiguration,
  };
}

const _removeModuleSensor = (configuration, slotToUpdate, moduleType, accessoryToRemove) => {
  // Deep copy the configuration
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));
  let targettedModule;
  if (moduleType === configurationModuleType.head_module) {
    targettedModule = duplicatedConfiguration.headModule;
  } else if (moduleType === configurationModuleType.io_module) {
    targettedModule = duplicatedConfiguration.ioModules.find(module => slotToUpdate === module.slot);
  } else if (moduleType === configurationModuleType.com_module) {
    targettedModule = duplicatedConfiguration.comModules.find(module => slotToUpdate === module.slot);
  } else if (moduleType === configurationModuleType.power_supply) {
    try {
      targettedModule = duplicatedConfiguration.powerSupplyManagement.powerSupplyGroups[slotToUpdate].powerSupply;
    } catch (error) { }
  } else if (moduleType === configurationModuleType.redbox) {
    duplicatedConfiguration.redboxes.forEach((redbox, index) => {
      if (redbox.slot === undefined) {
        redbox.slot = index;
      }
    });
    targettedModule = duplicatedConfiguration.redboxes.find(module => slotToUpdate === module.slot);
  }

  if (targettedModule) {
    // Keep only the needed accessories
    targettedModule.sensors = targettedModule.sensors.filter(accessory => accessoryToRemove !== accessory.accessory);
  }

  return {
    ...duplicatedConfiguration,
  };
}

const _removeModuleSparePart = (configuration, slotToUpdate, moduleType, accessoryToRemove) => {
  // Deep copy the configuration
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));
  let targettedModule;
  if (moduleType === configurationModuleType.head_module) {
    targettedModule = duplicatedConfiguration.headModule;
  } else if (moduleType === configurationModuleType.io_module) {
    targettedModule = duplicatedConfiguration.ioModules.find(module => slotToUpdate === module.slot);
  } else if (moduleType === configurationModuleType.com_module) {
    targettedModule = duplicatedConfiguration.comModules.find(module => slotToUpdate === module.slot);
  } else if (moduleType === configurationModuleType.power_supply) {
    try {
      targettedModule = duplicatedConfiguration.powerSupplyManagement.powerSupplyGroups[slotToUpdate].powerSupply;
    } catch (error) { }
  } else if (moduleType === configurationModuleType.redbox) {
    duplicatedConfiguration.redboxes.forEach((redbox, index) => {
      if (redbox.slot === undefined) {
        redbox.slot = index;
      }
    });
    targettedModule = duplicatedConfiguration.redboxes.find(module => slotToUpdate === module.slot);
  }

  if (targettedModule) {
    // Keep only the needed accessories
    targettedModule.spareParts = targettedModule.spareParts.filter(accessory => accessoryToRemove !== accessory.accessory);
  }

  return {
    ...duplicatedConfiguration,
  };
}

const _removeModuleOption = (configuration, slotToUpdate, moduleType, accessoryToRemove) => {

  // Deep copy the configuration
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));
  let targettedModule;
  if (moduleType === configurationModuleType.head_module) {
    targettedModule = duplicatedConfiguration.headModule;
  } else if (moduleType === configurationModuleType.io_module) {
    targettedModule = duplicatedConfiguration.ioModules.find(module => slotToUpdate === module.slot);
  } else if (moduleType === configurationModuleType.com_module) {
    targettedModule = duplicatedConfiguration.comModules.find(module => slotToUpdate === module.slot);
  } else if (moduleType === configurationModuleType.power_supply) {
    try {
      targettedModule = duplicatedConfiguration.powerSupplyManagement.powerSupplyGroups[slotToUpdate].powerSupply;
    } catch (error) { }
  } else if (moduleType === configurationModuleType.redbox) {
    duplicatedConfiguration.redboxes.forEach((redbox, index) => {
      if (redbox.slot === undefined) {
        redbox.slot = index;
      }
    });
    targettedModule = duplicatedConfiguration.redboxes.find(module => slotToUpdate === module.slot);
  }

  if (targettedModule) {
    // Keep only the needed accessories
    targettedModule.options = targettedModule.options.filter(accessory => accessoryToRemove !== accessory.accessory);
  }

  return {
    ...duplicatedConfiguration,
  };
}

function _removeModuleOptionForCategory(configuration, accessoryType, targetSlot, moduleType, accessory) {
  let updatedConfig = configuration;
  switch (accessoryType) {
    case OPTION_CATEGORIES.ACCESSORIES:
      updatedConfig = _removeModuleAccessory(configuration, targetSlot, moduleType, accessory);
      break;
    case OPTION_CATEGORIES.AUXILIARIES:
      updatedConfig = _removeModuleAuxiliary(configuration, targetSlot, moduleType, accessory);
      break;
    case OPTION_CATEGORIES.SENSORS:
      updatedConfig = _removeModuleSensor(configuration, targetSlot, moduleType, accessory);
      break;
    case OPTION_CATEGORIES.SPARE_PARTS:
      updatedConfig = _removeModuleSparePart(configuration, targetSlot, moduleType, accessory);
      break;
    case OPTION_CATEGORIES.OPTIONS:
      updatedConfig = _removeModuleOption(configuration, targetSlot, moduleType, accessory);
      break;
    case OPTION_CATEGORIES.WARRANTIES:
      updatedConfig = _setModuleWarranty(configuration, targetSlot, moduleType, null);
      break;
    default:
      break;
  }
  return updatedConfig;
}

function _removeModuleOptionForConfiguration(configuration, rackLocation, accessoryType, targetSlot, moduleType, accessory) {
  let updatedConfig = JSON.parse(JSON.stringify(configuration));

  if (rackLocation && configuration.localRacks[rackLocation - 1]) {
    const correctRackLocation = rackLocation - 1;
    updatedConfig.localRacks = configuration.localRacks.map((localRack, idx) => {
      if (idx === correctRackLocation) {
        return _removeModuleOptionForCategory(localRack, accessoryType, targetSlot, moduleType, accessory);
      } else {
        return localRack;
      }
    });
  } else {
    updatedConfig = _removeModuleOptionForCategory(updatedConfig, accessoryType, targetSlot, moduleType, accessory);
  }

  return updatedConfig;
}

export function removeModuleOption(configuration, rackLocation, accessoryType, targetSlot, moduleType, accessory) {
  let updatedConfig = JSON.parse(JSON.stringify(configuration));

  updatedConfig = _removeModuleOptionForConfiguration(configuration, rackLocation, accessoryType, targetSlot, moduleType, accessory);

  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}

export function removeIslandModuleOption(configuration, islandSlot, rackLocation, accessoryType, ...args) {
  let updatedConfig = JSON.parse(JSON.stringify(configuration));

  updatedConfig.ioIslands.map(island => {
    if(island.slot === islandSlot) {
      const islandConfig = island.configuration;
      island.configuration = _removeModuleOptionForConfiguration(islandConfig, rackLocation, accessoryType, ...args);
    }

    return island;
  });

  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}


/*** IO MODULE ***************/

const _addIOModules = (configuration, mod, quantity) => {
  const modulesTMP = [...configuration.ioModules];
  for (let i = 0; i < quantity; ++i) {
    const newModule = Object.assign({}, mod);
    newModule.slot = modulesTMP.map(d => d.slot).reduce((a, b) => a > b ? a : b, 0) + 1;
    modulesTMP.push(newModule);
  }
  return {
    ...JSON.parse(JSON.stringify(configuration)),
    ioModules: modulesTMP
  };
}
export function addIOModules(configuration, ...args) {
  const updatedConfig = _addIOModules(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}

/*** COM MODULE **************/
const _addComModules = (configuration, mod, quantity, rackLocation) => {

  if (rackLocation && configuration.localRacks[rackLocation - 1]) {
    const correctRackLocation = rackLocation - 1;
    configuration.localRacks = configuration.localRacks.map((localRack, idx) => {
      if (idx === correctRackLocation) {
        return _addComModules(localRack, mod, quantity, null);
      } else {
        return localRack;
      }
    });

    return JSON.parse(JSON.stringify(configuration));
  }

  const modulesTMP = [...configuration.comModules];
  for (let i = 0; i < quantity; ++i) {
    const newModule = Object.assign({}, mod);
    newModule.slot = modulesTMP.map(d => d.slot).reduce((a, b) => a > b ? a : b, 0) + 1;
    modulesTMP.push(newModule);
  }

  // For PAC, when we add a power supply, if the rack is redundant, fill all slots with the power supply we try to add.
  if (configuration?.starterType === possible_starter_selections.PAC) {
    const currentFilledSlots = modulesTMP.map(mod => mod.slot).sort();
    for (let i = 1; i <= configuration?.rack?.maxPowerSupplies; i++) {
      if (!currentFilledSlots.includes(i)) {
        const newModule = Object.assign({}, mod);
        newModule.slot = i;
        modulesTMP.push(newModule);
      }
    }
  }

  return {
    ...JSON.parse(JSON.stringify(configuration)),
    comModules: modulesTMP,
  };
}
export function addComModules(configuration, ...args) {
  const updatedConfig = _addComModules(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}

/*** CARTRIDGE ***************/

const _addCartridge = (configuration, mod, slot) => {
  const cartridgesTMP = configuration.cartridges.filter(c => c.slot !== slot);
  mod.slot = slot;
  cartridgesTMP.push(mod);
  return {
    ...JSON.parse(JSON.stringify(configuration)),
    cartridges: cartridgesTMP,
  };
}
export function addCartridge(configuration, ...args) {
  const updatedConfig = _addCartridge(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}

const _removeCartridge = (configuration, slot) => {
  const cartridgesTMP = [...configuration.cartridges.filter(c => c.slot !== slot)];
  return {
    ...configuration,
    cartridges: cartridgesTMP,
  };
}
export function removeCartridge(configuration, ...args) {
  const updatedConfig = _removeCartridge(configuration, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}

/*** IO ISLAND ***************/

const DEFAULT_IO_ISLAND = {
  id: null,
  status: null,
  senderEmail: null,
  senderName: null,
  step: null,
  starterType: null,
  name: 'My I/O island',
  rangeInfo: null,
  headModule: null,
  ioModules: [],
  comModules: [],
  cartridges: [],
  softwareVersion: null,
}

export function addNewIOIsland(configuration, headModule, protocolName, filters) {
  const usedSlots = configuration.ioIslands.map(isl => isl.slot);
  const newIOIslandSlot = Math.max(...[0, ...usedSlots]) + 1;

  let newName = `${I10n.getOrDefault('io_island.default_name', DEFAULT_IO_ISLAND.name, 'data')} ${newIOIslandSlot}`;
  const alreadyExistingNameAmount = configuration.ioIslands.filter(isl => isl?.configuration?.name === newName).length;
  if (alreadyExistingNameAmount > 0) {
    newName += ` (${alreadyExistingNameAmount})`;
  }

  const newIOIsland = {
    slot: newIOIslandSlot,
    protocol: { name: protocolName, },
    configuration: {
      ...DEFAULT_IO_ISLAND,
      name: newName,
      headModule,
      rangeInfo: { rangeName: headModule.rangeOfProduct, },
      starterType: possible_starter_selections.IO_ONLY,
      softwareVersion: headModule.defaultSoftwareVersion(),
    },
    customOutputConsumption: 0
  };

  const ioIslandsTMP = [...configuration.ioIslands];
  ioIslandsTMP.push(newIOIsland);

  return (dispatch, getState) => {
    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
      ioIslands: ioIslandsTMP,
    }
    return updateConfiguration(newConfiguration, dispatch, getState, true).then(() => {
      logFilterSelectedEvent(newConfiguration, filter_types.io_island, filters);
    });
  }
}

export const duplicateIsland = (slot, defaultNameKey = 'io_island') => {
  return async (dispatch, getState) => {
    const configurationToUpdate = JSON.parse(JSON.stringify(getState().configuration));
    const island = configurationToUpdate.ioIslands.find(island => island.slot === slot);
    if (!island) {
      throw new Error(`Island with slot ${slot} not found`);
    }

    const duplicatedIsland = JSON.parse(JSON.stringify(island));

    const usedSlots = configurationToUpdate.ioIslands.map(isl => isl.slot);
    const newIOIslandSlot = Math.max(...[0, ...usedSlots]) + 1;
    duplicatedIsland.slot = newIOIslandSlot;

    let newName = `${I10n.getOrDefault(`${defaultNameKey}.default_name`, DEFAULT_IO_ISLAND.name, 'data')} ${newIOIslandSlot}`;
    const alreadyExistingNameAmount = configurationToUpdate.ioIslands.filter(isl => isl.configuration.name === newName).length;
    if (alreadyExistingNameAmount > 0) {
      newName += ` (${alreadyExistingNameAmount})`;
    }
    duplicatedIsland.configuration.name = newName

    const newConfiguration = {
      ...configurationToUpdate,
      ioIslands: [...configurationToUpdate.ioIslands, duplicatedIsland]
    };

    return updateConfiguration(newConfiguration, dispatch, getState, true);
  };
};

export const duplicateRemote = (slot) => {
  return duplicateIsland(slot, 'pac_extension_rack');
};

export function removeIOIsland(configuration, slot) {
  return (dispatch, getState) => {
    const ioIslandsTMP = [...configuration.ioIslands.filter(c => c.slot !== slot)];
    ioIslandsTMP.forEach(isl => {
      if (isl.slot < slot || slot < 1) return;
      isl.slot = isl.slot - 1;
    });
    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
      ioIslands: ioIslandsTMP,
    };
    return updateConfiguration(newConfiguration, dispatch, getState, true);
  }
}

export function renameIOIsland(configuration, slot, configurationName) {
  return (dispatch, getState) => {
    const ioIslandsTMP = configuration.ioIslands;
    ioIslandsTMP.forEach(isl => {
      if (isl.slot !== slot) return;
      isl.configuration.name = configurationName;
    });
    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
      ioIslands: ioIslandsTMP,
    };
    return updateConfiguration(newConfiguration, dispatch, getState, false);
  }
}

const updateIsland = (configuration, slot, updateAction, ...args) => {
  const island = configuration.ioIslands.find(isl => isl.slot === slot);
  const islandConfig = island?.configuration;
  const updatedIslandConfig = updateAction(islandConfig, ...args);
  const ioIslandsTMP = [...configuration.ioIslands];
  ioIslandsTMP.filter(isl => isl.slot === slot).forEach(isl => isl.configuration = updatedIslandConfig);
  return {
    ...JSON.parse(JSON.stringify(configuration)),
    ioIslands: ioIslandsTMP,
  };
};

export function addIslandModules(configuration, slot, ...args) {
  const updatedConfig = updateIsland(configuration, slot, _addIOModules, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}
export function addIslandComModules(configuration, slot, ...args) {
  const updatedConfig = updateIsland(configuration, slot, _addComModules, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}
export function addIslandCartridge(configuration, slot, ...args) {
  const updatedConfig = updateIsland(configuration, slot, _addCartridge, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}
export function removeIslandCartridge(configuration, slot, ...args) {
  const updatedConfig = updateIsland(configuration, slot, _removeCartridge, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}
export function duplicateIslandModule(configuration, slot, ...args) {
  const updatedConfig = updateIsland(configuration, slot, _duplicateModule, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}
export function removeIslandModule(configuration, slot, ...args) {
  const updatedConfig = updateIsland(configuration, slot, _removeModule, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}
export function insertIslandModulesBefore(configuration, slot, ...args) {
  const updatedConfig = updateIsland(configuration, slot, _insertModulesBefore, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}
export function insertIslandModulesAfter(configuration, slot, ...args) {
  const updatedConfig = updateIsland(configuration, slot, _insertModulesAfter, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}
export function replaceIslandModule(configuration, slot, ...args) {
  const updatedConfig = updateIsland(configuration, slot, _replaceModule, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}
export function updateIslandModule(configuration, slot, ...args) {
  const updatedConfig = updateIsland(configuration, slot, _updateModule, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}
export function setIslandModuleWarranty(configuration, slot, ...args) {
  const updatedConfig = updateIsland(configuration, slot, _setModuleWarranty, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}
export function addIslandModuleAccessory(configuration, slot, ...args) {
  const updatedConfig = updateIsland(configuration, slot, _addModuleAccessory, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}
export function addIslandModuleAuxiliary(configuration, slot, ...args) {
  const updatedConfig = updateIsland(configuration, slot, _addModuleAuxiliary, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}
export function addIslandModuleSensor(configuration, slot, ...args) {
  const updatedConfig = updateIsland(configuration, slot, _addModuleSensor, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}
export function addIslandModuleSparePart(configuration, slot, ...args) {
  const updatedConfig = updateIsland(configuration, slot, _addModuleSparePart, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}
export function addIslandModuleOption(configuration, slot, ...args) {
  const updatedConfig = updateIsland(configuration, slot, _addModuleOption, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, false);
}

export function replaceIslandSelectedAdditionalDevice(configuration, slot, ...args) {
  const updatedConfig = updateIsland(configuration, slot, _replaceSelectedAdditionalDevice, ...args);
  return (dispatch, getState) => updateConfiguration(updatedConfig, dispatch, getState, true);
}

/**************************************************************************************************/

/**
 * Functions for power supply management.
 */

export function setIOIslandCustomOutputConsumption(configuration, islandSlot, newCustomOutputConsumption) {
  return (dispatch, getState) => {
    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };

    newConfiguration.powerSupplyManagement.powerSupplyGroups.forEach(group => {
      group.powerSupply = null;
    });


    if (islandSlot === 0) {
      newConfiguration.customOutputConsumption = newCustomOutputConsumption;
    } else {
      // Should only contain one element.
      const islandsToUpdate = newConfiguration.ioIslands.filter(c => c.slot === islandSlot);
      islandsToUpdate.forEach(island => {
        island.configuration.customOutputConsumption = newCustomOutputConsumption;
      });
      newConfiguration.powerSupplyManagement.powerSupplyGroups.forEach(group => {
        group.powerSupply = null;
      });
    }

    return updateConfiguration(newConfiguration, dispatch, getState, false);
  }
}

export function setPowerSupplyStep(configuration, step, withSave = true) {
  return (dispatch, getState) => {
    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };

    newConfiguration.powerSupplyManagement.step = step;

    if (withSave) {
      return updateConfiguration(newConfiguration, dispatch, getState, false);
    } else {
      return new Promise((successCallback, failureCallback) => {
        dispatch(restoreConfiguration(newConfiguration));
        successCallback();
      });
    }
  }
}

export function addPowerSupplyGroup() {
  return (dispatch, getState) => {
    const configuration = getState().configuration;
    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };

    newConfiguration.powerSupplyManagement.powerSupplyGroups.push(
      new PowerSupplyGroup({
        name: I10n.getAndReplace(`screen.power_supply_screen.default_group_name`, newConfiguration.powerSupplyManagement.powerSupplyGroups.length + 1),
        powerSupply: null,
        consumption: 0,
        islandsSlots: []
      })
    );

    return updateConfiguration(newConfiguration, dispatch, getState, false);
  }
}

export function removePowerSupplyGroup(idxToRemove) {
  return (dispatch, getState) => {
    const configuration = getState().configuration;
    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };

    const newGroups = [];
    newConfiguration.powerSupplyManagement.powerSupplyGroups.forEach((group, idx) => {
      if (idx !== idxToRemove) {
        newGroups.push(group);
      }
    });

    newConfiguration.powerSupplyManagement.powerSupplyGroups = newGroups;

    return updateConfiguration(newConfiguration, dispatch, getState, false);
  }
}

export function renamePowerSupplyGroup(newName, idxToRename) {
  return (dispatch, getState) => {
    const configuration = getState().configuration;
    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };

    try {
      newConfiguration.powerSupplyManagement.powerSupplyGroups[idxToRename].name = newName;
    } catch (error) { }

    return updateConfiguration(newConfiguration, dispatch, getState, false);
  }

}

export function moveIslandBetweenPowerSupplyGroups(groupOrigin, groupDestination, islandSlot) {

  return (dispatch, getState) => {
    const configuration = getState().configuration;

    try {
      configuration.powerSupplyManagement.powerSupplyGroups[groupOrigin].islandsSlots = configuration.powerSupplyManagement.powerSupplyGroups[groupOrigin].islandsSlots.filter(slot => slot !== islandSlot);
      configuration.powerSupplyManagement.powerSupplyGroups[groupOrigin].powerSupply = null;
      configuration.powerSupplyManagement.powerSupplyGroups[groupDestination].islandsSlots.push(islandSlot);
      configuration.powerSupplyManagement.powerSupplyGroups[groupDestination].powerSupply = null;
    } catch (error) {
      console.log(error);
    }

    return updateConfiguration(configuration, dispatch, getState, false);
  }

}

export function setPowerSupplies(powerSuppliesPayload) {

  return (dispatch, getState) => {
    const configuration = getState().configuration;
    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };

    try {
      newConfiguration.powerSupplyManagement.powerSupplyGroups.forEach((group, idx) => {
        if (powerSuppliesPayload[idx].isAdded === true && powerSuppliesPayload[idx].id != null) {
          group.powerSupply = new PowerSupply({ id: powerSuppliesPayload[idx].id });
        } else {
          group.powerSupply = null;
        }
      });

    } catch (error) {
      console.log(error);
    }

    return _preSaveConfiguration(newConfiguration, configurationStatuses.finalized, configuration.step, dispatch, getState, false);
  }
}

export function setPowerSupplyRequirements(requirements) {

  return (dispatch, getState) => {
    const configuration = getState().configuration;
    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };

    try {
      newConfiguration.powerSupplyManagement.requirements = requirements;
      newConfiguration.powerSupplyManagement.step = powerSupplySteps.consumption_estimation;
      newConfiguration.powerSupplyManagement.powerSupplyGroups.forEach(group => {
        group.powerSupply = null;
      });
    } catch (error) {
      console.log(error);
    }

    return updateConfiguration(newConfiguration, dispatch, getState, false);
  }

}

/**************************************************************************************************/

/**
 * SUB ACCESSORY
 */

const _addSubAccessoryToConfiguration = (configuration, slotsToUpdate, moduleType, newAccessory, subAccessory, quantity = 1) => {
  // Deep copy the configuration
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));

  if (moduleType === configurationModuleType.head_module) {
    duplicatedConfiguration.headModule = _addSubAccessoryToModule(duplicatedConfiguration.headModule, newAccessory.optionCategory, newAccessory, subAccessory, quantity);
  } else if (moduleType === configurationModuleType.io_module) {
    duplicatedConfiguration.ioModules = duplicatedConfiguration.ioModules.map(module => {
      if (slotsToUpdate.includes(module.slot)) {
        module.accessories = _insertModuleAccessory(module.accessories, newAccessory, quantity);
        module.accessories = module.accessories.map(accessory => {
          if (accessory.accessory === newAccessory) {
            accessory.subAccessories = _insertModuleSubAccessory(accessory.subAccessories, subAccessory, quantity);
          }
          return accessory;
        });
      }
      return module;
    });
  } else if (moduleType === configurationModuleType.com_module) {
    duplicatedConfiguration.comModules = duplicatedConfiguration.comModules.map(module => {
      if (slotsToUpdate.includes(module.slot)) {
        module.accessories = _insertModuleAccessory(module.accessories, newAccessory, quantity);
        module.accessories = module.accessories.map(accessory => {
          if (accessory.accessory === newAccessory) {
            accessory.subAccessories = _insertModuleSubAccessory(accessory.subAccessories, subAccessory, quantity);
          }
          return accessory;
        });
      }
      return module;
    });
  } else if (moduleType === configurationModuleType.power_supply) {
    if (duplicatedConfiguration.powerSupplyManagement && duplicatedConfiguration.powerSupplyManagement.powerSupplyGroups) {
      duplicatedConfiguration.powerSupplyManagement.powerSupplyGroups.forEach((powerSupplyGroup, idx) => {
        if (slotsToUpdate.includes(idx)) {
          const targettedModule = powerSupplyGroup.powerSupply;
          if (targettedModule) {
            targettedModule.options.forEach(option => {
              if (option.accessory === newAccessory.id) {
                option.subAccessories = _insertModuleSubAccessory(option.subAccessories, subAccessory.id, quantity);
              }
            });
          }
        }
      });
    }
  }

  return duplicatedConfiguration;
}

const _removeSubAccessoryFromModule = (mod, optionCategory, newAccessory, subAccessory) => {
  if (mod[optionCategory]) {
    mod[optionCategory].forEach(option => {
      if (option.accessory === newAccessory.id) {
        option.subAccessories = option.subAccessories.filter(subAcc => subAcc.accessory !== subAccessory.id);
      }
    });
  }
  return mod;
}

const _removeSubAccessoryFromConfiguration = (configuration, slotsToUpdate, moduleType, newAccessory, subAccessory) => {
  if (moduleType === configurationModuleType.head_module) {
    configuration.headModule = _removeSubAccessoryFromModule(configuration.headModule, newAccessory.optionCategory, newAccessory, subAccessory);
  } else if (moduleType === configurationModuleType.power_supply) {
    if (configuration.powerSupplyManagement && configuration.powerSupplyManagement.powerSupplyGroups) {
      configuration.powerSupplyManagement.powerSupplyGroups.forEach((powerSupplyGroup, idx) => {
        if (slotsToUpdate.includes(idx)) {
          const targettedModule = powerSupplyGroup.powerSupply;
          if (targettedModule) {
            targettedModule.options.forEach(option => {
              if (option.accessory === newAccessory.id) {
                option.subAccessories = option.subAccessories.filter(subAcc => subAcc.accessory !== subAccessory.id);
              }
            });
          }
        }
      });
    }
  }
  return configuration;
}

const _addSubAccessoryToModuleList = (moduleList, slotsToAdd, moduleId, optionCategory, newAccessory, subAccessory, quantity = 1) => {
  moduleList.forEach(mod => {
    if (mod.id === moduleId && slotsToAdd.includes(mod.slot)) {
      _addSubAccessoryToModule(mod, optionCategory, newAccessory, subAccessory, quantity);
    }
  });
  return moduleList;
}

const _addSubAccessoryToModule = (mod, optionCategory, newAccessory, subAccessory, quantity = 1) => {
  if (mod[optionCategory]) {
    mod[optionCategory].forEach(option => {
      if (option.accessory === newAccessory.id) {
        option.subAccessories = _insertModuleSubAccessory(option.subAccessories, subAccessory.id, quantity);
      }
    });
  }

  return mod;
}

const _addSubAccessory  = (configuration, slotsToUpdate, moduleType, newAccessory, subAccessory, quantity = 1, islandSlot, rackLocation) => {
  // Deep copy the configuration
  const duplicatedConfiguration = JSON.parse(JSON.stringify(configuration));

  if (islandSlot === 0) {
    if (rackLocation && duplicatedConfiguration.localRacks[rackLocation - 1]) {
      const correctRackLocation = rackLocation - 1;
      duplicatedConfiguration.localRacks = duplicatedConfiguration.localRacks.map((localRack, idx) => {
        if (idx === correctRackLocation) {
          return _addSubAccessoryToConfiguration(localRack, slotsToUpdate, moduleType, newAccessory, subAccessory, quantity);
        } else {
          return localRack;
        }
      });
    } else {
      return _addSubAccessoryToConfiguration(duplicatedConfiguration, slotsToUpdate, moduleType, newAccessory, subAccessory, quantity);
    }
  } else {
    duplicatedConfiguration.ioIslands.map(island => {
      if (island.slot === islandSlot) {
        if (rackLocation && island.configuration.localRacks[rackLocation - 1]) {
          const correctRackLocation = rackLocation - 1;
          island.configuration.localRacks = island.configuration.localRacks.map((localRack, idx) => {
            if (idx === correctRackLocation) {
              return _addSubAccessoryToConfiguration(localRack, slotsToUpdate, moduleType, newAccessory, subAccessory, quantity);
            } else {
              return localRack;
            }
          });
        } else {
          island.configuration = _addSubAccessoryToConfiguration(island.configuration, slotsToUpdate, moduleType, newAccessory, subAccessory, quantity);
        }
      }
      return island;
    });
  }

  return {
    ...duplicatedConfiguration,
  };
}


const _removeSubAccessory = (configuration, slotsToUpdate, moduleType, accessoryToRemove, subAccessoryToRemove, islandSlot = 0, rackLocation = null) => {

  let newConfiguration = new Configuration(JSON.parse(JSON.stringify(configuration)));

  if (islandSlot === 0) {
    if (rackLocation && newConfiguration.localRacks[rackLocation - 1]) {
      const correctRackLocation = rackLocation - 1;
      newConfiguration.localRacks = newConfiguration.localRacks.map((localRack, idx) => {
          if (idx === correctRackLocation) {
              return _removeSubAccessoryFromConfiguration(localRack, slotsToUpdate, moduleType, accessoryToRemove, subAccessoryToRemove);
          } else {
              return localRack;
          }
      });
      return newConfiguration;
    } else {
      return _removeSubAccessoryFromConfiguration(newConfiguration, slotsToUpdate, moduleType, accessoryToRemove, subAccessoryToRemove);
    }
  } else {
    newConfiguration.ioIslands = newConfiguration.ioIslands.map(island => {
      if (island.slot === islandSlot) {
        if (rackLocation && island.configuration.localRacks[rackLocation - 1]) {
          const correctRackLocation = rackLocation - 1;
          island.configuration.localRacks = island.configuration.localRacks.map((localRack, idx) => {
            if (idx === correctRackLocation) {
                return _removeSubAccessoryFromConfiguration(localRack, slotsToUpdate, moduleType, accessoryToRemove, subAccessoryToRemove);
            } else {
                return localRack;
            }
          });
        } else {
          island.configuration = _removeSubAccessoryFromConfiguration(island.configuration, slotsToUpdate, moduleType, accessoryToRemove, subAccessoryToRemove);
        }
      }
      return island;
    });
    newConfiguration = {
        ...JSON.parse(JSON.stringify(configuration)),
        ioIslands: newConfiguration.ioIslands,
    };
  }

  return newConfiguration;
};

export function setShowPrecommercializedProducts(newStatus) {

  return (dispatch, getState) => {
    const configuration = getState().configuration;

    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };

    newConfiguration.showPrecommercializedProducts = newStatus;

    return _preSaveConfiguration(newConfiguration, newConfiguration.status, newConfiguration.step, dispatch, getState, false);
  }
}

/**************************************************************************************************/

/**
 * Reducer and action used to save the configuration in DynamoDB.
 */
const DEFAULT_STATE = { isSaving: false, error: null };

export const saveConfigurationStarted = createAction('SAVE_CONFIGURATION_STARTED');
export const saveConfigurationSuccess = createAction('SAVE_CONFIGURATION_SUCCESS');
export const saveConfigurationFailed = createAction('SAVE_CONFIGURATION_FAILED');

export const savedConfigurationReducer = createReducer(function (on, off) {
  on(saveConfigurationStarted, (state, payload) => {
    return {
      isSaving: true,
      error: null,
    }
  });
  on(saveConfigurationSuccess, (state, payload) => {
    return {
      isSaving: false,
      error: null,
    }
  });
  on(saveConfigurationFailed, (state, payload) => {
    return {
      isSaving: false,
      error: null,
    }
  });
}, DEFAULT_STATE);

function buildHost() {
  let host = appConfiguration.domain;
  if (appConfiguration.seamlessSession) {
    host += '/myschneider';
  }
  if (appConfiguration.unifiedCheckoutAvailable) {
    host += '/unified-checkout';
  } else if (appConfiguration.isWorkspaceAvailable) {
    host += '/workspace';
  } else if (appConfiguration.launchFromWorkspace) {
    host += '/from-hmc';
  }
  return host;
}

// Create a new configuration with a given PLC module
export function createConfigurationWithModule(headModule, filters) {
  return (dispatch, getState) => {
    dispatch(saveConfigurationStarted());
    const configuration = getState().configuration;

    const seenSteps = [...(configuration.seenSteps || [])];
    seenSteps.push(stepsConfig.head_device_selector_screen, stepsConfig.configuration_editor_screen);

    const newConfiguration = {
      starterType: configuration.starterType,
      headModule: new DeviceDTO(headModule),
      name: I10n.getOrDefault('configuration.default_name', DEFAULT_CONFIGURATION.name, 'data'),
      rangeInfo: { rangeName: headModule.rangeOfProduct },
      rack: null,
      host: buildHost(),
      country: getAppLocaleAsObject().country,
      step: stepsConfig.configuration_editor_screen,
      status: configurationStatuses.finalized,
      customOutputConsumption: 0,
      campaignId: appConfiguration.campaignId,
      internal: appConfiguration.isPublicInternal || (appConfiguration.seamlessSession !=null  && appConfiguration.seamlessSession.isInternal === true),
      urlParams: extractUrlParameters(),
      seenSteps
    };


    if (appConfiguration.seamlessSession && appConfiguration.seamlessSession.userChannels) {
      newConfiguration.userChannels = appConfiguration.seamlessSession.userChannels;
    }

    const configurationDTO = new ConfigurationDTO(newConfiguration);
    const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
    const seamlessIdString = appConfiguration.seamlessSession ? `&seamlessId=${appConfiguration.seamlessSession.id}` : '';

    return API_INSTANCE
      .post(`${api_base_url}/configurations?locale=${getAppLocale()}${mainOverrideString}${seamlessIdString}`, {
        ...configurationDTO,
      }).then((success) => {
        if (filters) {
          logFilterSelectedEvent(success.data.payload.configuration, filter_types.plc, filters);
        }
        dispatch(restoreConfiguration(success.data.payload.configuration));
        logVirtualPageViewDataLayerEvent(success.data.payload.configuration, stepsConfig.configuration_editor_screen);
        dispatch(saveConfigurationSuccess());
        return success.data.payload;
      }).catch((error) => {
        dispatch(saveConfigurationFailed());
        console.log(error);
        throw error;
      });
  }
}

function extractUrlParameters() {

  let search;

  try {
    search = window.parent.location.search;
  } catch (error) {
    search = window.location.search;
  }
  const urlParams = new URLSearchParams(search);
  const paramsWithValue = [];
  urlParams.forEach((value, key) => {
    paramsWithValue.push(key + "=" + value);
  });
  return paramsWithValue.join("&");
}


// Replace the configuration head module and clean all io modules / islands / com modules
export function setConfigurationHeadModule(headModule, filters) {
  return (dispatch, getState) => {
    const configuration = getState().configuration;

    const seenSteps = [...(configuration.seenSteps || [])];
    seenSteps.push(stepsConfig.head_device_selector_screen, stepsConfig.configuration_editor_screen);

    const newConfiguration = {
      id: configuration.id,
      starterType: configuration.starterType,
      headModule: new DeviceDTO(headModule),
      name: I10n.getOrDefault('configuration.default_name', DEFAULT_CONFIGURATION.name, 'data'),
      rangeInfo: { rangeName: headModule.rangeOfProduct },
      rack: null,
      host: buildHost(),
      country: getAppLocaleAsObject().country,
      step: stepsConfig.configuration_editor_screen,
      status: configurationStatuses.finalized,
      customOutputConsumption: 0,
      campaignId: appConfiguration.campaignId,
      internal: appConfiguration.isPublicInternal || (appConfiguration.seamlessSession != null && appConfiguration.seamlessSession.isInternal === true),
      urlParams: extractUrlParameters(),
      seenSteps
    };

    return updateConfiguration(newConfiguration, dispatch, getState, true).then((success) => {
      logVirtualPageViewDataLayerEvent(newConfiguration, stepsConfig.configuration_editor_screen);
      logFilterSelectedEvent(newConfiguration, filter_types.plc, filters);
      return success;
    }).catch(error => {
      throw error;
    });
  }
}

// Create a configuration without a PLC module
export function createEmptyConfiguration() {
  return (dispatch, getState) => {
    dispatch(saveConfigurationStarted());
    const configuration = getState().configuration;

    const seenSteps = [...(configuration.seenSteps || [])];
    seenSteps.push(stepsConfig.head_device_selector_screen);

    const newConfiguration = {
      name: I10n.getOrDefault('configuration.default_name', DEFAULT_CONFIGURATION.name, 'data'),
      configurationType: null,
      softwareSelection: null,
      servicePlanActivated: true,
      showPrecommercializedProducts: false,
      licenseModel: null,
      softwareInfo: null,
      starterType: configuration.starterType,
      rack: null,
      host: buildHost(),
      country: getAppLocaleAsObject().country,
      campaignId: appConfiguration.campaignId,
      internal: appConfiguration.isPublicInternal || (appConfiguration.seamlessSession != null && appConfiguration.seamlessSession.isInternal === true),
      urlParams: extractUrlParameters(),
      seenSteps
    };

    if (appConfiguration.seamlessSession && appConfiguration.seamlessSession.userChannels) {
      newConfiguration.userChannels = appConfiguration.seamlessSession.userChannels;
    }

    const configurationDTO = new ConfigurationDTO(newConfiguration);
    const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
    const seamlessIdString = appConfiguration.seamlessSession ? `&seamlessId=${appConfiguration.seamlessSession.id}` : '';

    return API_INSTANCE
      .post(`${api_base_url}/configurations?locale=${getAppLocale()}${mainOverrideString}${seamlessIdString}`, {
        ...configurationDTO,
      }).then((success) => {
        dispatch(restoreEmptyConfiguration(success.data.payload.configuration));
        logVirtualPageViewDataLayerEvent(success.data.payload.configuration, stepsConfig.head_device_selector_screen);
        dispatch(saveConfigurationSuccess());
        return success.data.payload;
      }).catch((error) => {
        dispatch(saveConfigurationFailed());
        throw error;
      });
  }
}

export function updateConfiguration(configuration, dispatch, getState, clearPowerSupplies = false) {
  let status = configurationStatuses.finalized;
  const allowSavedModifications = getState()?.configuration?.allowSavedModifications;

  if (appConfiguration.seamlessSession && appConfiguration.seamlessSession.userChannels) {
    configuration.userChannels = appConfiguration.seamlessSession.userChannels;
  }
  if (appConfiguration.campaignId) {
    configuration.campaignId = appConfiguration.campaignId;
  }
  configuration.internal = (appConfiguration.seamlessSession != null && appConfiguration.seamlessSession.isInternal === true) || appConfiguration.isPublicInternal;
  configuration.urlParams = extractUrlParameters();

  const configurationDTO = new ConfigurationDTO(configuration);


  if (clearPowerSupplies) {
    // Clear power supplies
    if (configurationDTO.powerSupplyManagement && configurationDTO.powerSupplyManagement.powerSupplyGroups) {
      configurationDTO.powerSupplyManagement.step = powerSupplySteps.requirements;
      configurationDTO.powerSupplyManagement.requirements = {};
      configurationDTO.powerSupplyManagement.powerSupplyGroups.forEach(group => {
        group.powerSupply = null;
      });
      // Clear power supply step
    }
  }

  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
  const seamlessIdString = appConfiguration.seamlessSession ? `&seamlessId=${appConfiguration.seamlessSession.id}` : '';
  const allowSavedModificationsString = allowSavedModifications ? `&allowSavedModifications=true` : '';

  configurationDTO.ioIslands.forEach(isl => {
    isl.configuration.status = status;
  })

  dispatch(saveConfigurationStarted());
  return API_INSTANCE
    .post(`${api_base_url}/configurations/${configuration.id}?locale=${getAppLocale()}${mainOverrideString}${seamlessIdString}${allowSavedModificationsString}`, {
      ...configurationDTO,
      status: status,
      step: configuration.step,
      host: buildHost(),
      country: getAppLocaleAsObject().country
    }).then((success) => {
      dispatch(restoreConfiguration(success.data.payload.configuration))
      dispatch(saveConfigurationSuccess());
      return success.data.payload;
    }).catch((error) => {
      dispatch(saveConfigurationFailed());
      throw error;
    });
}

function _preSaveConfiguration(configuration, status, step, dispatch, getState, activateSavedModications = false) {

  let newStatus = status;
  if (appConfiguration.seamlessSession && appConfiguration.seamlessSession.userChannels) {
    configuration.userChannels = appConfiguration.seamlessSession.userChannels;
  }
  if (appConfiguration.campaignId) {
    configuration.campaignId = appConfiguration.campaignId;
  }
  configuration.internal = (appConfiguration.seamlessSession != null && appConfiguration.seamlessSession.isInternal === true) || appConfiguration.isPublicInternal;
  configuration.urlParams = extractUrlParameters();

  const seenSteps = [...(configuration.seenSteps || [])];
  seenSteps.push(step);

  configuration.seenSteps = seenSteps;

  const configurationDTO = new ConfigurationDTO(configuration);

  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
  const seamlessIdString = appConfiguration.seamlessSession ? `&seamlessId=${appConfiguration.seamlessSession.id}` : '';
  const oldConfigurationStatus = configuration.status;

  // Allow user to modify his saved configuration without creating a duplicate if he is still modifying it before leaving
  let enableSavedModifications = false;
  if (oldConfigurationStatus !== status && configurationStatuses.finished === status) {
    enableSavedModifications = true;
  }

  const allowSavedModifications = getState()?.configuration?.allowSavedModifications;
  // Allow the modification of cancelled or obsolete configurations
  if (allowSavedModifications && configurationDTO.status === configurationStatuses.finished) {
    newStatus = configurationStatuses.finished;
  }

  const allowSavedModificationsString = allowSavedModifications ? `&allowSavedModifications=true` : '';

  configurationDTO.ioIslands.forEach(isl => {
    isl.configuration.status = newStatus;
  });

  dispatch(saveConfigurationStarted());
  return API_INSTANCE
    .post(`${api_base_url}/configurations/${configuration.id}?locale=${getAppLocale()}${mainOverrideString}${seamlessIdString}${allowSavedModificationsString}`, {
      ...configurationDTO,
      status: newStatus,
      step: step,
      host: buildHost(),
      country: getAppLocaleAsObject().country
    }).then((success) => {

      const config = success.data.payload.configuration;
      if (enableSavedModifications || activateSavedModications) {
        config.allowSavedModifications = true;
        config.showSurveyDisclaimer = true;
      }

      dispatch(restoreConfiguration(config));
      logVirtualPageViewDataLayerEvent(config, step);
      dispatch(saveConfigurationSuccess());
      return success.data.payload;
    }).catch((error) => {
      dispatch(saveConfigurationFailed());
      throw error;
    });

}

export function preSaveConfiguration(configuration, status = configurationStatuses.finished, step = stepsConfig.save_screen) {
  return (dispatch, getState) => {
    return _preSaveConfiguration(configuration, status, step, dispatch, getState, false);
  }
}

function _saveConfiguration(configId, configuration, senderEmail, senderName, captchaResponse, isWrapperMode, dispatch, getState) {
  dispatch(saveConfigurationStarted());

  const newConfiguration = {
    ...configuration,
    senderEmail: senderEmail,
    senderName: senderName,
    host: buildHost(),
    country: getAppLocaleAsObject().country,
    campaignId: appConfiguration.campaignId,
    internal: appConfiguration.isPublicInternal || (appConfiguration.seamlessSession != null && appConfiguration.seamlessSession.isInternal === true),
    urlParams: extractUrlParameters(),
    seenSteps: [...(configuration.seenSteps || [])]
  };

  let newStatus = configurationStatuses.finished;
  if (appConfiguration.seamlessSession && appConfiguration.seamlessSession.userChannels) {
    configuration.userChannels = appConfiguration.seamlessSession.userChannels;
  }

  const configurationDTO = new ConfigurationDTO(newConfiguration);


  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
  const seamlessIdString = appConfiguration.seamlessSession ? `&seamlessId=${appConfiguration.seamlessSession.id}` : '';

  const allowSavedModifications = getState()?.configuration?.allowSavedModifications;

  const allowSavedModificationsString = allowSavedModifications ? `&allowSavedModifications=true` : '';

  configurationDTO.ioIslands.forEach(isl => {
    isl.configuration.status = newStatus;
  })

  return API_INSTANCE
    .post(`${api_base_url}/configurations/${configId}?locale=${getAppLocale()}${mainOverrideString}${seamlessIdString}${allowSavedModificationsString}`, {
      ...configurationDTO,
      status: newStatus,
    }).then((success) => {
      logSavedConfigurationEvent(configuration);
      dispatch(restoreConfiguration(success.data.payload.configuration))
      dispatch(saveConfigurationSuccess());
      if (!isWrapperMode) {
        shareConfiguration(configId, senderEmail, senderName, null, null, null, captchaResponse, false);
      }
      return success.data.payload;
    }).catch((error) => {
      dispatch(saveConfigurationFailed());
      throw error;
    });
}

export function saveConfiguration(configId, configuration, senderEmail, senderName, captchaResponse, isWrapperMode) {
  return (dispatch, getState) => {
    return _saveConfiguration(configId, configuration, senderEmail, senderName, captchaResponse, isWrapperMode, dispatch, getState);
  }
}

// Create a configuration without a PLC module
export function beforeLogout() {
  return (dispatch, getState) => {
    const configuration = getState().configuration;
    const isConfigurationInProgress = (configuration.status === configurationStatuses.finalized || !configuration.step === stepsConfig.start_screen);
    const cancelFunction = isConfigurationInProgress ? () => { _cancelConfiguration(configuration.id) } : () => { }
    return { isConfigurationInProgress, cancelFunction };
  }
}

// Synchronize public website configurations on MySchneider account.
export function synchronizeMySchneiderConfigurations() {

  console.log('Try to synchronize with My Schneider');

  const seamlessIdString = appConfiguration.seamlessSession ? `?seamlessId=${appConfiguration.seamlessSession.id}` : '';

  return API_INSTANCE
    .post(`${api_base_url}/synchronize-my-schneider${seamlessIdString}`, {
    }).then(() => {
      console.log('Synchro worked as expected');
      return true;
    }).catch((error) => {
      throw error;
    });
}


/*** SOFTWARE LICENCE ***********/

function _createSoftwareLicenceConfiguration (softwareSelection, step, dispatch, getState) {
  dispatch(saveConfigurationStarted());
  const configuration = getState().configuration;

  const seenSteps = [];
  seenSteps.push(softwareStepsConfig.software_selection);

  const newConfiguration = {
    name: I10n.getOrDefault('configuration.default_name', DEFAULT_CONFIGURATION.name, 'data'),
    starterType: configuration.starterType,
    configurationType: possible_starter_selections.SOFTWARE_LICENCE,
    softwareSelection,
    servicePlanActivated: true,
    showPrecommercializedProducts: false,
    licenseModel: LICENSE_MODELS.PERPETUAL,
    step,
    rack: null,
    host: buildHost(),
    country: getAppLocaleAsObject().country,
    campaignId: appConfiguration.campaignId,
    internal: appConfiguration.isPublicInternal || (appConfiguration.seamlessSession != null && appConfiguration.seamlessSession.isInternal === true),
    urlParams: extractUrlParameters(),
    seenSteps
  };

  if (appConfiguration.seamlessSession && appConfiguration.seamlessSession.userChannels) {
    newConfiguration.userChannels = appConfiguration.seamlessSession.userChannels;
  }

  const configurationDTO = new ConfigurationDTO(newConfiguration);
  const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
  const seamlessIdString = appConfiguration.seamlessSession ? `&seamlessId=${appConfiguration.seamlessSession.id}` : '';

  return API_INSTANCE
    .post(`${api_base_url}/configurations?locale=${getAppLocale()}${mainOverrideString}${seamlessIdString}`, {
      ...configurationDTO,
    }).then((success) => {
      dispatch(restoreEmptyConfiguration(success.data.payload.configuration));
      logVirtualPageViewDataLayerEvent(success.data.payload.configuration, step);
      dispatch(saveConfigurationSuccess());
      return success.data.payload;
    }).catch((error) => {
      dispatch(saveConfigurationFailed());
      throw error;
    });
}

export function createEmptySoftwareLicenceConfiguration() {
  return (dispatch, getState) => {
    return _createSoftwareLicenceConfiguration(null, softwareStepsConfig.software_selection, dispatch, getState);
  }
}

export function createSoftwareLicenceConfigurationFromSoftware(softwareSelection) {
  return (dispatch, getState) => {
    return _createSoftwareLicenceConfiguration(softwareSelection, softwareStepsConfig.licence_selector, dispatch, getState);
  }
}

export function setSoftwareSelection(softwareSelection, nextStep) {

  return (dispatch, getState) => {
    const configuration = getState().configuration;

    // Clear previously inserted software licences
    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
      softwareLicences: [],
      softwareLicenseSelections: [],
      licenseModel: LICENSE_MODELS.PERPETUAL,
      softwareSelection
    };

    return _preSaveConfiguration(newConfiguration, configurationStatuses.finalized, nextStep, dispatch, getState, false);
  }
}

export function addSoftwareLicences(licencesWithQty, selectorOrigin) {
  return (dispatch, getState) => {
    const configuration = getState().configuration;

    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };
    newConfiguration.softwareLicences = buildNewSoftwareLicenses(newConfiguration.softwareLicences, licencesWithQty, selectorOrigin);

    return updateConfiguration(newConfiguration, dispatch, getState, false);
  }
}

export function addSoftwareLicencesWithNavigation(licencesWithQty, selectorOrigin) {
  return (dispatch, getState) => {
    const configuration = getState().configuration;

    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };

    newConfiguration.softwareLicences = buildNewSoftwareLicenses(newConfiguration.softwareLicences, licencesWithQty, selectorOrigin);

    if (newConfiguration.servicePlanItems.length > 0) {
      return _preSaveConfiguration(newConfiguration, configurationStatuses.finalized, softwareStepsConfig.service_plan, dispatch, getState, false);
    }

    return _preSaveConfiguration(newConfiguration, configurationStatuses.finished, softwareStepsConfig.save_screen, dispatch, getState, true);
  }
}

export function removeSoftwareLicence(licence) {
  return (dispatch, getState) => {
    const configuration = getState().configuration;

    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };

    newConfiguration.softwareLicences = removeSoftwareLicenceHelper(newConfiguration.softwareLicences, licence);

    return updateConfiguration(newConfiguration, dispatch, getState, false);
  }
}

export function setSoftwareLicenceQuantity(licence, newQuantity) {
  return (dispatch, getState) => {
    const configuration = getState().configuration;

    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };

    newConfiguration.softwareLicences = setSoftwareLicenceQuantityHelper(newConfiguration.softwareLicences,
      licence, newQuantity);

    return updateConfiguration(newConfiguration, dispatch, getState, false);
  }
}

export function setServicePlanActivation(newStatus) {

  return (dispatch, getState) => {
    const configuration = getState().configuration;

    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };

    newConfiguration.servicePlanActivated = newStatus;

    return _preSaveConfiguration(newConfiguration, configurationStatuses.finished, softwareStepsConfig.save_screen, dispatch, getState, true);
  }
}

export function setServicePlanActivationServicePlanScreen(newStatus) {

  return (dispatch, getState) => {
    const configuration = getState().configuration;

    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };

    newConfiguration.servicePlanActivated = newStatus;

    return _preSaveConfiguration(newConfiguration, configurationStatuses.finalized, configuration.step, dispatch, getState, true);
  }
}

export function setLicenseModel(newLicenceModel) {

  return (dispatch, getState) => {
    const configuration = getState().configuration;

    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };

    newConfiguration.licenseModel = newLicenceModel;

    // Remove the license which license type is different from the new license type.
    newConfiguration.softwareLicences = newConfiguration.softwareLicences.filter(license => license.licenseModel === newLicenceModel);

    return updateConfiguration(newConfiguration, dispatch, getState, false);
  }
}

export function setHardwareToSoftwareSelection(selectedSoftware, newLicenceModel, licenses, servicePlanActivationStatus) {
  return (dispatch, getState) => {
    const configuration = getState().configuration;

    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };

    let shouldInsertSelection = true;
    newConfiguration.softwareLicenseSelections
      .filter((softwareSelection) => {
        return softwareSelection.softwareSelection === selectedSoftware;
      }).forEach((softwareSelection) => {
        // If the software selection has already been inserted, replace its content.
        softwareSelection.softwareSelection = selectedSoftware;
        softwareSelection.licenseModel = newLicenceModel;
        softwareSelection.softwareLicences = licenses;
        softwareSelection.servicePlanActivated = servicePlanActivationStatus;
        shouldInsertSelection = false;
      });

    if (shouldInsertSelection) {
      newConfiguration.softwareLicenseSelections.push(
        {
          softwareSelection: selectedSoftware,
          licenseModel: newLicenceModel,
          softwareLicences: licenses,
          servicePlanActivated: servicePlanActivationStatus
        }
      );
    }

    return updateConfiguration(newConfiguration, dispatch, getState, false);
  }

}

export function clearSoftwareLicenseInformations(newStep, softwareToClear) {
  return (dispatch, getState) => {
    const configuration = getState().configuration;

    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };

    newConfiguration.step = newStep;
    newConfiguration.softwareLicenseSelections = newConfiguration.softwareLicenseSelections
      .filter((softwareSelection) => {
        return softwareSelection.softwareSelection !== softwareToClear;
      });

    return updateConfiguration(newConfiguration, dispatch, getState, false);
  }
}

export function clearSoftwareLicenses() {
  return (dispatch, getState) => {
    const configuration = getState().configuration;

    const newConfiguration = {
      ...JSON.parse(JSON.stringify(configuration)),
    };

    newConfiguration.step = softwareStepsConfig.licence_selector;
    newConfiguration.softwareLicences = [];

    return updateConfiguration(newConfiguration, dispatch, getState, false);
  }
}

export function updateMsxSoftwareLicenseReferences(configuration) {
  return (dispatch, getState) => {
    const allowSavedModifications = getState()?.configuration?.allowSavedModifications;

    if (appConfiguration.seamlessSession && appConfiguration.seamlessSession.userChannels) {
      configuration.userChannels = appConfiguration.seamlessSession.userChannels;
    }
    if (appConfiguration.campaignId) {
      configuration.campaignId = appConfiguration.campaignId;
    }
    configuration.internal = (appConfiguration.seamlessSession != null && appConfiguration.seamlessSession.isInternal === true) || appConfiguration.isPublicInternal;
    configuration.urlParams = extractUrlParameters();

    const configurationDTO = new ConfigurationDTO(configuration);

    const mainOverrideString = appConfiguration.main ? `&main=${appConfiguration.main}` : '';
    const seamlessIdString = appConfiguration.seamlessSession ? `&seamlessId=${appConfiguration.seamlessSession.id}` : '';
    const allowSavedModificationsString = allowSavedModifications ? `&allowSavedModifications=true` : '';



    dispatch(saveConfigurationStarted());
    return API_INSTANCE
      .post(`${api_base_url}/configurations/${configuration.id}/update-software-licenses?locale=${getAppLocale()}${mainOverrideString}${seamlessIdString}${allowSavedModificationsString}`, {
        ...configurationDTO,
        status: configurationStatuses.finalized,
        step: softwareStepsConfig.licence_selector,
        host: buildHost(),
        country: getAppLocaleAsObject().country
      }).then((success) => {
        dispatch(restoreConfiguration(success.data.payload.configuration))
        dispatch(saveConfigurationSuccess());
        return success.data.payload;
      }).catch((error) => {
        dispatch(saveConfigurationFailed());
        throw error;
      });
  };
}
